/**
 * App entry: builds the shell, wires the repo bar, and renders the inventory.
 *
 * Selecting a tensor range-reads its bytes (whole rows, deterministically
 * sampled above the size cap) and hands them to a worker for analysis. Nothing
 * is fetched until it is clicked.
 */

import {
  type Anatomy,
  buildAnatomy,
  cellKey,
  layerGroupOf,
  modelParamCount,
  toLogicalTensors,
} from "./analysis/anatomy.js";
import { describeExl3, findExl3Groups } from "./analysis/exl3-trellis.js";
import { type InventoryDiff, diffInventories } from "./analysis/inventory-diff.js";
import { findAffineGroups } from "./analysis/mlx.js";
import { describePackedSidecar, findPackedIntGroups } from "./analysis/packed-int.js";
import { type TowerFit, chooseTower } from "./analysis/tower-match.js";
import {
  SHARED_LABEL,
  type Tower,
  defaultTower,
  detectTowers,
  openingTower,
  towerOf,
} from "./analysis/towers.js";
import { describeUnknownTypes } from "./gguf/inventory.js";
import { ggufRepoSummary } from "./gguf/repo-summary.js";
import { costOf, fetchVariantSizes } from "./gguf/variant-sizes.js";
import {
  autoGgufVariant,
  findGgufVariants,
  modelVariants,
  variantAsComponent,
} from "./gguf/variants.js";
import { chooseComponent, isGgufRepo } from "./hf/choose-component.js";
import { componentLabel, partToReopen } from "./hf/component-label.js";
import { type RepoComponent, autoComponent, findComponents } from "./hf/components.js";
import {
  HubGatedError,
  type ResolvedRepo,
  isCanonicalRepoId,
  normalizeRepoId,
  resolveRepo,
} from "./hf/hub.js";
import { readCachedInventory, writeCachedInventory } from "./hf/inventory-cache.js";
import { prepareShareCard } from "./hf/prepare-card.js";
import { type Provenance, readProvenance } from "./hf/provenance.js";
import { describeQuantization, explainUndecodable } from "./hf/quant-config.js";
import { type RowRun, foldTo2D, planTensorRead } from "./hf/read-plan.js";
import {
  fetchAffineTriple,
  fetchAffineTripleAtRuns,
  fetchPackedGroup,
  fetchTensorBytes,
  fetchTensorBytesAtRuns,
} from "./hf/tensor-fetch.js";
import { buildContext } from "./report/context.js";
import { REPORT_ENABLED } from "./report/flag.js";
import {
  ComponentChoiceRequired,
  type Inventory,
  UnsupportedFormat,
  readInventory,
  tallyByDtype,
} from "./safetensors/inventory.js";
import { SITE_BUILD } from "./site-build.js";
import { type AnalysisOutcome, presentOutcome } from "./ui/analysis-outcome.js";
import {
  analysisForSelection,
  busyMessage,
  keepsResultsWhileWorking,
} from "./ui/analysis-pane.js";
import {
  type AnatomyMetric,
  type CompareScope,
  type PartLine,
  type TowerScope,
  cellDetail,
  cellTypeNotes,
  defaultMetric,
  enhanceAnatomyMap,
  renderAnatomyMap,
} from "./ui/anatomy-map.js";
import { betaStickerHtml } from "./ui/beta-sticker.js";
import { cardTitle } from "./ui/card.js";
import { footerHtml, headerHtml } from "./ui/chrome.js";
import { openPartDialog, openSuggestionDialog } from "./ui/compare-dialog.js";
import { renderInventoryDiff, renderTensorComparison } from "./ui/compare-view.js";
import { discoverySvg } from "./ui/discovery.js";
import { explainForDtype } from "./ui/dtype-explanations.js";
import { explainIcon, explainIconFor, wireExplain } from "./ui/explain.js";
import { formatBytes, formatParams, formatShape, shortSha } from "./ui/format.js";
import { introHtml, landingNotesHtml } from "./ui/intro.js";
import { openReportDialog } from "./ui/report-dialog.js";
import { attachRovingFocus, setRovingTabindex } from "./ui/roving-focus.js";
import { openShareDialog } from "./ui/share-dialog.js";
import {
  CLIP_GRID_PROGRESS_ID,
  type TensorViewModel,
  WHAT_IF_PROGRESS_ID,
  clipGridProgressText,
  renderTensorAnalysis,
  whatIfProgressText,
} from "./ui/tensor-view.js";
import { mountThemeToggle } from "./ui/theme.js";
import {
  SITE_ORIGIN,
  type UrlState,
  buildUrl,
  canonicalUrl,
  parseUrlState,
} from "./ui/url-state.js";
import { AnalysisClient } from "./worker/client.js";
import type { CompareSide } from "./worker/protocol.js";
import "./styles/tokens.css";
import "./styles/app.css";
import { documentTitle } from "./ui/document-title.js";

/**
 * The title `index.html` shipped with, captured before anything overwrites it.
 *
 * Not a constant here: that would be a fifth copy of a string this file already
 * counts copies of (see the empty-state comment below), and the copies drifted
 * apart once already. Reading the document makes the default true by
 * construction.
 */
const LANDING_TITLE = document.title;

/**
 * Three suggestions, chosen so each one reaches a code path the others cannot.
 *
 *  - `gpt2` — small, old, plain F32, one file. The fastest possible first
 *    click, and the flat case: one dtype everywhere, which is what makes the
 *    anatomy map open on bytes rather than on a uniform grid.
 *  - `Qwen3.5-9B` — **four shards and an index**, so it is the one that
 *    exercises the sharded header path: the index is read, then each shard's
 *    header in turn, sequentially and never in parallel (plan §5.3). Also the
 *    only one large enough to show that an inventory's cost tracks the number
 *    of files rather than the size of the model.
 *  - `Qwen3.5-4B-OptiQ-4bit` — **mixed precision**, and the reason this slot
 *    exists. Its config carries 249 per-module overrides: `embed_tokens` and
 *    the QKV projections at 8 bits, the rest at 4. So its bits-per-weight map
 *    actually *varies*, which is what plan §4 asked the allocation map for —
 *    "the quantization decisions, not just their effects". A uniformly-4-bit
 *    repo paints one flat colour and demonstrates nothing. It is also
 *    multimodal and sharded, so it reaches quantized decode, effective
 *    bits-per-weight, the tower split and the shard path at once.
 *
 * All public and ungated. They are suggestions only — nothing is fetched until
 * one is clicked (plan §5.5), and even then only headers.
 *
 * An earlier version of this comment claimed one example was sharded when none
 * were; it was corrected to say so, and this set makes it true again. Check
 * before editing this list: "one of these is sharded" is a claim about the
 * repos, not about the code, and it goes stale silently.
 */
const EXAMPLE_REPOS = [
  "openai-community/gpt2",
  // Centre position on purpose: it is both the widest label and the most
  // interesting first click — the quantized, multimodal one whose map actually
  // varies. A centred row reads outward from the middle, so the widest chip
  // there keeps the row symmetric and puts the best example where the eye lands.
  "mlx-community/Qwen3.5-4B-OptiQ-4bit",
  "Qwen/Qwen3.5-9B",
];

interface AppState {
  inventory: Inventory | undefined;
  selectedTensor: string | undefined;
  filter: string;
  /**
   * The stack the whole view is showing (`Tower.id`), or `undefined` on a model
   * that has only one.
   *
   * **There is no "all".** A layer index only means something inside its own
   * stack, so a grid built from every tower merges audio layer 3, text layer 3
   * and vision layer 3 into one cell — "all" was not a wider view of the model,
   * it was three models added together. See `cellKey` in `analysis/anatomy.ts`.
   * So a multimodal checkpoint always has exactly one stack selected, defaulting
   * to text (`defaultTower`), and a single-stack checkpoint has no chips and no
   * selection to make.
   *
   * **One selection, two views.** It drives the list *and* the map together;
   * they are two renderings of the same choice, not two filters that can
   * disagree. Presentation over metadata already in hand — changing it never
   * reads a byte, and never changes what is selected.
   *
   * Deliberately *not* in the URL, exactly like `filter`: a shared link names a
   * model and a tensor, and the state of a view control is not part of what was
   * shared. See `ui/url-state.ts`.
   */
  tower: string | undefined;
  /**
   * The whole checkpoint's folded parameter count, computed once per load.
   *
   * Cached rather than recomputed because `modelParamCount` folds the entire
   * inventory, and both consumers — the status line and the tower block's
   * "(of 27.4B)" — would otherwise pay for that on every render, including
   * every chip click. It cannot go stale within a load: it is a property of an
   * immutable commit.
   */
  modelParams: number | undefined;
  /** Rendered HTML for the current tensor, or a status string while working. */
  analysis: string | undefined;
  /**
   * Which tensor `analysis` was rendered for.
   *
   * So a re-analysis can tell "the visitor picked a different tensor" from "the
   * same tensor, with another panel switched on". Only the first invalidates
   * what is on screen; see `analysisBusy`.
   */
  analysisTensor: string | undefined;
  /**
   * What `analysis` was rendered from.
   *
   * Kept so the pane can be re-rendered in a different *state* — specifically
   * with the what-if card showing a spinner — without re-reading anything. The
   * HTML string alone cannot be re-stated, only replaced.
   */
  analysisModel: TensorViewModel | undefined;
  /**
   * A recompute is running *under* results that are still true.
   *
   * The what-if sweep is eighteen passes over the weights and the clip grid is
   * forty-eight — seconds on a large tensor — and blanking the pane for that
   * long threw away a histogram that had not changed and was not being
   * recomputed. The visitor asked for an *additional* panel, so the answer they
   * already have stays up while the extra one is worked out.
   */
  analysisBusy: boolean;
  /**
   * Why the analysis pane has no plot — and **which kind of nothing** it is.
   *
   * A discriminated kind rather than a bare string, because the two are not the
   * same event and were being styled as though they were. `error` is something
   * that went wrong; `unsupported` is a scheme we chose not to implement yet,
   * where nothing failed and the visitor did nothing wrong. That one wore a red
   * "Error:" banner, and three visitors filed bug reports against it in the
   * first hours the report button existed.
   *
   * Encoded as a type rather than fixed at each site (reviewer, 2026-08-13): a
   * convention relies on someone remembering, and this is the second time a
   * chosen refusal has been mis-styled. The kind now decides both the pane's
   * chrome and the status tone, in one place, so the two cannot disagree.
   */
  analysisOutcome: AnalysisOutcome | undefined;
  logScale: boolean;
  /** The 48-pass clip-grid sweep is opt-in; see the worker protocol. */
  clipGrid: boolean;
  /** The 18-cell what-if sweep is opt-in; measured at 10.5 s on a 32M tensor. */
  whatIf: boolean;
  /**
   * The models a repo offers when it holds more than one, and none was named.
   *
   * Held as state rather than thrown away with the error because the list *is*
   * the response: a pipeline repo has no primary component, so the page shows
   * the choice instead of a failure.
   */
  componentChoice: readonly RepoComponent[] | undefined;
  /**
   * A read is in flight.
   *
   * Tracked rather than inferred, because "no inventory" is three different
   * situations — nothing asked for yet, a question to answer, and a read that
   * has not finished — and the view owes a different screen to each.
   */
  loading: boolean;
  /** Whether `componentChoice` lists models or quantizations of one model. */
  choiceKind: "models" | "quantizations" | undefined;
  /** Built from metadata alone once an inventory lands; no weights fetched. */
  anatomy: Anatomy | undefined;
  anatomyMetric: AnatomyMetric;
  /** Parsed once per repo from config.json; drives both the map and decode. */
  quantization: Awaited<ReturnType<typeof describeQuantization>>;

  // --- Compare mode (plan §4 v2) ---
  //
  // B is a whole second repo, so it needs its own everything: its inventory
  // decides which tensors are even comparable, and its own quantization config
  // decides how its bytes decode. Sharing A's would silently dequantize B with
  // A's scheme, which is the failure mode that looks entirely plausible.
  /** True once the visitor has opened the second slot, even before B loads. */
  compareOpen: boolean;
  compareRepo: ResolvedRepo | undefined;
  compareInventory: Inventory | undefined;
  /**
   * Which model inside B, when B holds several — B's answer to A's `?part=`.
   *
   * Kept in state rather than read off B's inventory at write time because it
   * must survive B being re-read: the part is the visitor's answer to a
   * question, and re-asking it on every reload would make a shared comparison
   * un-reproducible in exactly the way `?rev=` exists to prevent.
   */
  comparePart: string | undefined;
  compareQuantization: Awaited<ReturnType<typeof describeQuantization>>;
  /**
   * B's grid, on the same tower as A's — what puts a second number in every
   * cell of the map.
   *
   * **Zero extra Hub requests, and that is the whole reason this exists.** An
   * A-against-B map of *weight* deltas would mean reading every tensor from
   * both repos, which the no-crawling invariant forbids outright. But the four
   * metrics the map draws — storage dtype, bits per weight, bytes, parameters —
   * are all functions of safetensors headers plus `config.json`, and compare
   * mode already has both for B. So this is pure arithmetic over bytes in hand.
   * `undefined` when the two models' towers cannot be lined up; see
   * `compareScopeFor`, which then says why on screen.
   */
  compareAnatomy: Anatomy | undefined;
  /** Why B contributes no per-cell values, when it does not. */
  compareDeclined: string | undefined;
  /**
   * Which of B's stacks the grid is, when B has stacks.
   *
   * Separate from `state.tower`, which is **A's** selection, because the whole
   * point of the correspondence is that the two can differ: a text tower lifted
   * out of its parent has no stacks at all, so A's selection is `undefined`
   * while B's is `text`. Reading one field for both was the bug —
   * `compareScopeFor` matched by id equality and could only ever line up stacks
   * that were already named alike.
   */
  compareTower: string | undefined;
  /**
   * The evidence behind that choice, set **only** when it was chosen by
   * measurement rather than named by A.
   *
   * Carried so the page can state the fraction it acted on rather than assert a
   * match. Thin evidence then reads as thin — "19 of 19 cells" and "385 of 385"
   * are both a clean sweep, and a reader can see which one is worth anything —
   * without anyone choosing a second threshold to hide behind.
   */
  compareMatch: { readonly fit: TowerFit; readonly onSide: "a" | "b" } | undefined;
  /**
   * B's other stacks, when the comparison is scoped to one of them.
   *
   * Stated as a sentence and kept out of the diff population: those tensors
   * answer "what did I drop", which is a different question from "how does this
   * differ from the tower it came from", and several hundred one-sided rows
   * would bury the second under the first.
   */
  droppedTowers: readonly string[];
  /** Metadata-only, recomputed when either inventory changes. Costs no requests. */
  diff: InventoryDiff | undefined;
  /** Rendered HTML for the current tensor pair, or undefined while working. */
  comparison: string | undefined;
  comparisonError: string | undefined;
}

const state: AppState = {
  componentChoice: undefined,
  loading: false,
  choiceKind: undefined,
  inventory: undefined,
  selectedTensor: undefined,
  filter: "",
  tower: undefined,
  modelParams: undefined,
  analysis: undefined,
  analysisModel: undefined,
  analysisTensor: undefined,
  analysisBusy: false,
  analysisOutcome: undefined,
  logScale: false,
  clipGrid: false,
  whatIf: false,
  anatomy: undefined,
  anatomyMetric: "bpw",
  quantization: undefined,
  compareOpen: false,
  compareRepo: undefined,
  compareInventory: undefined,
  comparePart: undefined,
  compareQuantization: undefined,
  compareAnatomy: undefined,
  compareDeclined: undefined,
  compareTower: undefined,
  compareMatch: undefined,
  droppedTowers: [],
  diff: undefined,
  comparison: undefined,
  comparisonError: undefined,
};

/**
 * The URL the visitor arrived on, read once and up front: `writeUrlState` needs
 * it before the deep-link block at the bottom of this file runs, and reading
 * `location` twice invites the two reads disagreeing after a rewrite.
 */
const linked = resolveArrivalState();

/**
 * The state this page load is about — from a short link if it was one, and from
 * the address bar otherwise (SPEC-TINYURL §6.3).
 *
 * `/s/<hash>` is served **in place**: the responder emits the target model's
 * shell rather than redirecting, so that a crawler gets the card with nothing
 * to follow. That leaves the address bar reading `/s/<hash>` while the document
 * describes a model, and the resolved path arriving in a `data-` attribute.
 *
 * So this reads the attribute, uses it as the arrival state, and rewrites the
 * bar to the real deep link. `replaceState`, not `pushState`: the short URL must
 * not become a back-button stop, because going "back" to it would resolve the
 * same page again and trap the visitor.
 *
 * The attribute is escaped text the responder wrote, and it is parsed by the
 * same `parseUrlState` the address bar goes through — never used as a
 * navigation target on its own. `SITE_ORIGIN` is prefixed here, which is what
 * makes §2.1's "a short link cannot leave the origin" true at the last step as
 * well as at the first.
 */
function resolveArrivalState(): UrlState {
  const shared = document.body.dataset.tinyurlPath;
  if (shared === undefined || shared === "") {
    return parseUrlState(location.pathname, location.search);
  }
  const cut = shared.indexOf("?");
  const pathname = cut === -1 ? shared : shared.slice(0, cut);
  const search = cut === -1 ? "" : shared.slice(cut + 1);
  const state = parseUrlState(pathname, search);
  history.replaceState(null, "", `${SITE_ORIGIN}${buildUrl(state)}`);
  return state;
}

/**
 * `view` is part of the scheme (plan §22) but nothing reads it yet. Carry the
 * incoming value through every rewrite anyway: dropping a parameter we merely
 * don't understand would quietly break the first link that uses one.
 */
const urlView = linked.view;

const analysisClient = new AnalysisClient();

/**
 * Guards against a stale render. Clicking through a layer's tensors quickly
 * means several reads are in flight at once, and without this the slowest one
 * wins the screen — you click B, see B, then A's numbers replace them.
 */
let analysisToken = 0;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

/** Escape anything that came off the network before it reaches innerHTML. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// --- Shell ----------------------------------------------------------------

/**
 * What this document is, told to the shared chrome (`ui/chrome.ts`).
 *
 * The header and footer used to be written out here as markup and written again
 * by hand in `glossary.html`, and the two drifted in four visible ways inside a
 * day. They are one function now, and these three fields are the entire licence
 * this page has to differ from the other one.
 *
 * No `etiquetteHref`: in the app the footer glyph opens the tooltip, wired by
 * `wireExplain` below. The glossary passes a fragment instead, because there
 * the explanation is already on the page.
 */
const CHROME = {
  tagline: "safetensors, read in place",
  // The site's one <h1>, and the only element that can honestly hold it: the
  // brand lockup is what this page is. It is what stops the card headings below
  // from being a set of <h3>s hanging off nothing.
  brandIsHeading: true,
} as const;

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("#app is missing from index.html");

app.innerHTML = `
  <a class="skip-link" href="#tensor-view">Skip to content</a>
  ${headerHtml(CHROME)}

  <!--
    data-state here mirrors the one on <main> below. The loader's bottom rule
    belongs to the loaded screen, not the landing one (see the .repo-bar comment
    in app.css), and CSS cannot reach backwards from <main> to a preceding
    sibling without :has(). Carrying the state on both elements is a duplicate,
    and a deliberate one: renderView sets them together, and an explicit
    attribute is easier to reason about than a relational selector in a file
    that has already lost an afternoon to specificity.

    (No backticks in this comment: the whole shell is a template literal, and a
    backtick here closes it. Written with them the first time, exactly as the
    comment below warns.)
  -->
  <form class="repo-bar" id="repo-form" data-state="empty">
    <!--
      The A tag appears only once there is a B to distinguish it from. Labelling
      a lone input "A" would pose a question the visitor has not asked yet.
    -->
    <span class="slot-tag" data-slot="a" id="slot-tag-a" hidden aria-hidden="true">A</span>
    <!--
      Where model ids come from.
      -
      The input asks for a "Hugging Face model id" and, to anyone who has not
      used the Hub, gives no clue where one is found — so the first screen has a
      dead end on it for exactly the audience the landing copy is written for.
      This is an <a>, not a button: it navigates, so it should be a link, and
      being a link inside the form is also what stops a click from submitting.
      -
      The filter is deliberate: library=safetensors lists only models this tool
      can actually open, so nobody comes back with an id that reads as broken
      when it is merely a format we do not handle.
      -
      No CSP implication: this is a navigation, not a fetch, so connect-src is
      untouched and the promise it encodes still holds.
    -->
    <a
      class="btn btn--icon btn--find"
      id="hub-search"
      href="https://huggingface.co/models?library=safetensors&amp;sort=trending"
      target="_blank"
      rel="noopener noreferrer"
      title="Find a model on Hugging Face"
    >
      <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
        <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" stroke-width="1.8" />
        <line
          x1="10.4" y1="10.4" x2="14" y2="14"
          stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
        />
      </svg>
      <span class="visually-hidden">Find a model on Hugging Face (opens in a new tab)</span>
    </a>
    <input
      class="input input--repo mono"
      id="repo-input"
      name="repo"
      placeholder="Hugging Face model id, e.g. openai-community/gpt2"
      aria-label="Hugging Face model id"
      autocomplete="off"
      spellcheck="false"
    />
    <!--
      Which model inside the repo, when the repo holds several (owner,
      2026-08-12).

      Looks like a field and takes no typing. A part is a choice from a list the
      repo defines, never a string a visitor can compose — a text box would
      invite "Q4_K_M " or "q4_k_m" and answer with "not found" for what is
      plainly the right answer. So it is a button wearing the field's shape:
      the affordance says "this is part of the query", the behaviour says "pick
      one of these".

      Hidden until a repo that offers a choice is loaded, because until then it
      is a control with nothing behind it.
    -->
    <button
      class="input input--part mono"
      id="part-control"
      type="button"
      hidden
      aria-haspopup="dialog"
      aria-label="Which model inside this repo"
    ><span class="part-label"></span></button>
    <!--
      Two audiences, one line. "(optional)" tells someone who has never used the
      Hub that this box can be left alone — without it the field reads as a
      second thing to fill in before anything happens. "[main]" tells someone
      who has that the default is the main branch, which is the only question a
      seasoned reader has about a revision box.
    -->
    <input
      class="input input--rev mono"
      id="rev-input"
      name="rev"
      placeholder="Rev. (optional) [main]"
      aria-label="Revision — branch, tag or commit SHA. Optional; defaults to the main branch."
      autocomplete="off"
      spellcheck="false"
    />
    <!--
      Secondary first, primary last. The one action nearly everyone wants ends
      the row, where the eye finishes reading it and where a filled button is
      least likely to be mistaken for a heading.

      DOM order is the visual order, which is what keeps tab order honest — and
      the swap does not change what Enter does in the field: "Compare with…" is
      type="button", so the submit button is still the only one the form can
      default to, wherever it sits.
    -->
    <button class="btn" id="compare-toggle" type="button" disabled aria-expanded="false"
            aria-controls="compare-slot">Compare with…</button>
    <button class="btn btn--primary" id="load-button" type="submit" disabled>Explore</button>

    <div class="compare-slot" id="compare-slot" hidden>
      <span class="slot-tag" data-slot="b" aria-hidden="true">B</span>
      <input
        class="input input--repo mono"
        id="compare-repo-input"
        name="compare-repo"
        placeholder="second model id, e.g. its 4-bit quantization"
        aria-label="Second Hugging Face model id, compared against the first"
        autocomplete="off"
        spellcheck="false"
      />
      <button
        class="input input--part mono"
        id="compare-part-control"
        type="button"
        hidden
        aria-haspopup="dialog"
        aria-label="Which model inside the comparison repo"
      ><span class="part-label"></span></button>
      <input
        class="input input--rev mono"
        id="compare-rev-input"
        name="compare-rev"
        placeholder="Rev. (optional) [main]"
        aria-label="Revision for the second model — branch, tag or commit SHA. Optional; defaults to the main branch."
        autocomplete="off"
        spellcheck="false"
      />
      <!--
        No second submit button. "Explore" already means "read what is in this
        form", and with the slot open a separate "Compare" was a second control
        for one action — the visitor has to work out which of two buttons owns
        the row they just typed in. Enter in either field does the same thing,
        which is what people try first anyway.
      -->
      <button class="btn btn--icon btn--danger" id="compare-clear" type="button"
              aria-label="Remove the comparison model">✕</button>
    </div>


    <!--
      Two nodes, because one node cannot be both polite and assertive.
      Everything this page does after a click reports here — the load summary,
      "Reading bytes…", the arrival of a whole analysis pane — and until these
      carried live-region roles every one of it was silent to a screen reader
      (WCAG 2.2 AA 4.1.3). Progress and results are polite; a failure is
      assertive, because a visitor who pressed "Explore" and got nothing
      needs to know it failed rather than find out by waiting.

      Both are written by setStatus, which sets textContent on a node that
      outlives the message. That is what keeps each message announced exactly
      once: a live region re-created wholesale by a render announces itself
      again on arrival, and renderView replaces its pane on every state
      change.
    -->
    <div class="status-line">
      <output class="status" id="status" for="repo-input"
              role="status" aria-live="polite" aria-atomic="true"></output>
      <p class="status status--alert" id="status-alert" role="alert"
         aria-atomic="true"></p>
    </div>
  </form>

  <!--
    data-state drives the two-pane/one-pane switch in app.css. Before a model is
    loaded the tensor list has nothing to list, and a third of the viewport
    reading "No model loaded." next to the introduction is a poor first screen —
    so the landing state collapses to a single column and the pane comes back
    with the data. renderView() flips this attribute.

    (No backticks in this comment: the whole shell is a template literal, and a
    backtick here closes it.)
  -->
  <main class="app-main" data-state="empty">
    <div class="tensor-list-pane">
      <!--
        Visually hidden: the pane is unmistakable on screen and a visible
        heading here would be furniture. It exists to name the list group
        below, and to give the card headings in the other pane an h2 to sit
        under.
      -->
      <h2 class="visually-hidden" id="tensor-list-label">Tensors</h2>
      <input
        class="input mono"
        id="filter-input"
        placeholder="filter tensors…"
        aria-label="Filter tensors by name"
        style="width:100%;margin-bottom:var(--space-2)"
      />
      <!--
        Stack selector. Empty and hidden until a model turns out to have more
        than one layer stack, which is the only case where it means anything — a
        single-tower model must not grow a row of chrome that does nothing.
        renderTowerFilter() fills it; see analysis/towers.ts for what a tower is.

        Single-select, with no "all": it drives the tensor list and the anatomy
        map together, and a grid built from every stack at once merges three
        different layer 3s into one cell. See cellKey in analysis/anatomy.ts.

        A group of toggle buttons rather than a radiogroup: it is four buttons at
        most, so a second roving-tabindex context would cost more than the tab
        stops it saved, and aria-pressed reports the state a radio would.
      -->
      <div class="tower-filter" id="tower-filter" role="group"
           aria-label="Show one tower of this model" hidden></div>
      <div id="tensor-list" role="group" aria-labelledby="tensor-list-label"></div>
    </div>
    <div class="tensor-view-pane" id="tensor-view" tabindex="-1"></div>
  </main>

  ${footerHtml(CHROME)}
`;

const statusEl = document.querySelector<HTMLOutputElement>("#status");
const alertEl = document.querySelector<HTMLParagraphElement>("#status-alert");
const mainEl = document.querySelector<HTMLElement>(".app-main");
// The footer's one explain icon. Wired once — the footer is built with the
// shell and never re-rendered, unlike the panes.
const footerEl = document.querySelector<HTMLElement>(".app-footer");
if (footerEl) wireExplain(footerEl);
const listEl = document.querySelector<HTMLDivElement>("#tensor-list");
const viewEl = document.querySelector<HTMLDivElement>("#tensor-view");
const repoInput = document.querySelector<HTMLInputElement>("#repo-input");
const revInput = document.querySelector<HTMLInputElement>("#rev-input");
const filterInput = document.querySelector<HTMLInputElement>("#filter-input");
const towerFilterEl = document.querySelector<HTMLDivElement>("#tower-filter");
const loadButton = document.querySelector<HTMLButtonElement>("#load-button");
const themeToggle = document.querySelector<HTMLButtonElement>("#theme-toggle");
const repoForm = document.querySelector<HTMLFormElement>("#repo-form");
const compareToggle = document.querySelector<HTMLButtonElement>("#compare-toggle");
const compareSlot = document.querySelector<HTMLDivElement>("#compare-slot");
const compareRepoInput =
  document.querySelector<HTMLInputElement>("#compare-repo-input");
const compareRevInput = document.querySelector<HTMLInputElement>("#compare-rev-input");
const compareClear = document.querySelector<HTMLButtonElement>("#compare-clear");
const partControl = document.querySelector<HTMLButtonElement>("#part-control");
const comparePartControl = document.querySelector<HTMLButtonElement>(
  "#compare-part-control",
);
const slotTagA = document.querySelector<HTMLSpanElement>("#slot-tag-a");

if (
  !statusEl ||
  !alertEl ||
  !listEl ||
  !viewEl ||
  !repoInput ||
  !revInput ||
  !filterInput ||
  !towerFilterEl ||
  !loadButton ||
  !themeToggle ||
  !repoForm ||
  !compareToggle ||
  !compareSlot ||
  !compareRepoInput ||
  !compareRevInput ||
  !compareClear ||
  !slotTagA
) {
  throw new Error("Shell failed to build — a required element is missing");
}

mountThemeToggle(themeToggle);

/**
 * Line the status message up with the field it is about.
 *
 * Measured rather than declared. The distance from the bar's content edge to
 * the input is the width of whatever sits ahead of it, and that set changes: the
 * A slot tag appears when compare mode opens, and on a narrow screen the bar
 * wraps and the input starts at the content edge with nothing ahead of it at
 * all. A CSS rule would have to name both widths and guess the wrapping
 * breakpoint; subtracting two rectangles is correct in every case, including the
 * wrapped one, where the difference falls out as zero on its own.
 */
function alignStatusLine(): void {
  const bar = document.querySelector<HTMLElement>(".repo-bar");
  const line = document.querySelector<HTMLElement>(".status-line");
  if (!bar || !line || !repoInput) return;

  const barLeft =
    bar.getBoundingClientRect().left +
    Number.parseFloat(getComputedStyle(bar).paddingLeft);
  const input = repoInput.getBoundingClientRect();
  const indent = Math.max(0, Math.round(input.left - barLeft));
  const width = Math.round(input.width);

  // Only write on change: this runs from observers that the write itself can
  // trip, and an unconditional set would call this straight back forever.
  const nextIndent = `${indent}px`;
  if (line.style.getPropertyValue("--status-indent") !== nextIndent) {
    line.style.setProperty("--status-indent", nextIndent);
  }
  const nextWidth = `${width}px`;
  if (line.style.getPropertyValue("--status-width") !== nextWidth) {
    line.style.setProperty("--status-width", nextWidth);
  }

  alignCompareSlot(indent, width);
}

/**
 * Put row B's fields under row A's.
 *
 * The two rows carry different things — A has the Hub-search link before its
 * field and a wider pair of buttons after it, B has neither — so laid out
 * independently their columns cannot line up: B's model field started 42px to
 * the left of A's and ran wider, and the two revision boxes ended up nowhere
 * near each other. The row reads as a second, unrelated loader rather than the
 * same control repeated.
 *
 * Copied from A rather than declared, because flexbox has no way to say "as wide
 * as that box on the other line". Two independent flex rows share no column
 * geometry, and the alternative — a grid with `subgrid` — would mean giving up
 * the wrapping behaviour that makes this bar work on a phone, plus a breakpoint
 * guess about when to switch back.
 *
 * @param aIndent how far A's field sits from the bar's content edge
 * @param aWidth  how wide A's field is
 */
function alignCompareSlot(aIndent: number, aWidth: number): void {
  if (!compareSlot || !compareRepoInput || !revInput) return;

  // What already precedes B's field: its slot tag, plus the row's gap. The
  // margin makes up the difference to A's offset rather than replacing it, so
  // this never depends on the margin it is about to set.
  const tagB = compareSlot.querySelector<HTMLElement>(".slot-tag");
  const gap = Number.parseFloat(getComputedStyle(compareSlot).columnGap) || 0;
  const lead = tagB ? tagB.getBoundingClientRect().width + gap : 0;

  /*
   * Written on the **form**, not on `.compare-slot`.
   *
   * Custom properties inherit, and two of the form's children need these now:
   * B's fields, and the "made from" row under them, which is a sibling of the
   * slot rather than a child of it. Set on the slot, `--b-indent` was invisible
   * to that row and the suggestions sat flush left while the field they fill
   * was 30px in — measured on the dev server, 2026-08-12.
   *
   * Safe against the observers above: a custom property does not change the
   * bar's size, so the ResizeObserver stays quiet, and the MutationObserver
   * filters on `hidden`.
   */
  const host = repoForm ?? compareSlot;
  const set = (name: string, value: string) => {
    if (host.style.getPropertyValue(name) !== value) {
      host.style.setProperty(name, value);
    }
  };
  set("--b-indent", `${Math.max(0, Math.round(aIndent - lead))}px`);
  set("--b-repo-width", `${aWidth}px`);
  set("--b-rev-width", `${Math.round(revInput.getBoundingClientRect().width)}px`);
}

{
  const bar = document.querySelector<HTMLElement>(".repo-bar");
  if (bar) {
    // Two observers, because there are two ways the input moves and neither
    // sees the other's. Resizing covers the viewport changing and the bar
    // re-wrapping. It does *not* cover compare mode revealing the A slot tag:
    // that shifts the input right by the tag's width without changing the bar's
    // measured size in a way the ResizeObserver reports, so the indent stayed
    // put and the message sat 30px left of the field. Watching `hidden` catches
    // it. Both are safe to fire spuriously — `alignStatusLine` only writes when
    // the value actually changes, which is also what stops the style write from
    // re-triggering the MutationObserver forever.
    if (typeof ResizeObserver === "function") {
      new ResizeObserver(alignStatusLine).observe(bar);
    }
    if (typeof MutationObserver === "function") {
      new MutationObserver(alignStatusLine).observe(bar, {
        attributes: true,
        attributeFilter: ["hidden"],
        subtree: true,
      });
    }
  }
}
alignStatusLine();

// Attached to the container once, not to the rows: `renderList` replaces every
// row on every filter keystroke, and the container is what survives that.
/**
 * The inventory diff's tensor names, as one tab stop.
 *
 * Found by counting the shipped DOM against production: the diff table put
 * **200 separate tab stops** into the page, on a model the tensor list itself
 * renders as one. Every other homogeneous set here had already been collapsed —
 * 290 rows to one stop, 194 map cells to one — under the rule the accessibility
 * pass established, and this table was simply missed. Nothing in the source
 * hints at it; the count is only visible in a live browser.
 *
 * Attached to `viewEl` and not to the table, because the table does not survive
 * a re-render and `viewEl` does — the same reason the tensor list is wired on
 * its container. `attachRovingFocus` re-queries its items on every event, so a
 * table replaced underneath it keeps working.
 *
 * Selected by the data attribute rather than by `.link-button`: the class is a
 * look, and a second thing that happens to wear it should not silently join
 * this group.
 */
attachRovingFocus(viewEl, {
  itemSelector: "button[data-diff-tensor]",
  textOf: (button) => button.dataset.diffTensor ?? "",
});

attachRovingFocus(listEl, {
  itemSelector: ".tensor-row",
  // Typeahead matches what the row *shows*, not its full name. Rows are grouped
  // under their layer prefix and no longer repeat it, so matching on
  // `data-tensor` would mean typing `mlp` never hits a row whose full name
  // starts `model.layers.…` — a search that ignores the visible text.
  textOf: (row) => row.dataset.label ?? row.dataset.tensor ?? "",
});

/**
 * One call site, two live regions.
 *
 * An error goes to the assertive node and a success or progress note to the
 * polite one, and whichever is not being written is cleared — so only one
 * message is ever on screen and a stale error cannot outlive the failure it
 * described. Clearing is safe to do on every call: a live region announces
 * text that *arrives*, and removing text is not an arrival.
 */
/**
 * The one place a message reaches the status line.
 *
 * Two things here are accessibility requirements rather than style. **Severity
 * is prefixed in words**, because carrying it in `--bad` red alone fails WCAG
 * 1.4.1 — a reader who cannot see the colour gets a sentence with no indication
 * that anything went wrong. And **`error` and everything else use different
 * nodes**: the error node is `role="alert"` (assertive) and the other is
 * `role="status"` (polite), so a failure interrupts and a progress note does
 * not. Writing one always clears the other, or a stale error would sit under a
 * fresh success.
 *
 * `note` is deliberately not an error tone. A gated repo is the case plan §12.5
 * calls "a *feature* explanation, not an error": nothing failed, the model is
 * simply outside what a token-free tool can read. Painting it red would tell
 * the visitor they did something wrong.
 *
 * **The default is `working`, and it used to be `info`.** Every call site that
 * omits a tone is a progress message — resolving a revision, reading headers,
 * reading a tensor, running the what-if grid — so `info` was a name that said
 * nothing while the thing it described was always the same thing. Renaming it
 * changed no call site and gave the amber-plus-spinner treatment somewhere
 * honest to attach: the tone now *means* "still going", and a message that is
 * not still going has to say which of the other three it is.
 *
 * The spinner is CSS only (`.working` in app.css) and never reaches this
 * function. That is why the live region can be cleared and rewritten on every
 * shard without the animation stuttering — see the comment on the rule.
 */
/**
 * A caught thing's message, and whether it reads as a failure.
 *
 * **One decider, eight call sites.** Every catch in this file used to spell
 * `setStatus(error instanceof Error ? … , "error")` inline, so making one of
 * them distinguish a refusal from a failure would have meant editing eight and
 * missing one — the shape this project keeps getting wrong (ruling on one render
 * site when there were two, and again on one tower path when there were three).
 *
 * `UnsupportedFormat` is the only refusal today. Nothing went wrong when a repo
 * turns out to hold CoreML: the list was fetched and understood, and the limit
 * is ours. It takes the `note` tone the picker already uses for "a question, not
 * a failure". Everything else is still an error and still says so.
 */
function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function toneFor(error: unknown): "error" | "note" {
  return error instanceof UnsupportedFormat ? "note" : "error";
}

function setStatus(
  message: string,
  tone: "working" | "ok" | "error" | "note" = "working",
): void {
  if (!statusEl || !alertEl) return;
  if (tone === "error") {
    statusEl.textContent = "";
    writeMessage(alertEl, `Error: ${message}`);
    return;
  }
  alertEl.textContent = "";
  writeMessage(statusEl, tone === "note" ? `Note: ${message}` : message);
  statusEl.dataset.tone = tone;
}

/**
 * Render a status message, honouring two bits of structure in the source text:
 * a blank line starts a new block, and `**…**` is emphasis.
 *
 * Built as DOM nodes rather than assigned to `innerHTML`. Messages carry values
 * that came off the network or straight out of the input box — a repo id is
 * whatever the visitor typed — so the only safe way to add structure is to
 * create elements and set `textContent` on each, never to concatenate markup
 * and hope the escaping held.
 *
 * The two conventions exist because an error should lead with the one thing the
 * reader can act on. `RepoNotFoundError` says what failed, then, on its own
 * line and in bold, "Check the spelling." A reader scanning an error reads the
 * bold line and stops, which is the whole point of separating them.
 */
function writeMessage(target: HTMLElement, message: string): void {
  target.textContent = "";
  const blocks = message.split("\n\n");
  for (const [index, block] of blocks.entries()) {
    const line = index === 0 ? target : target.appendChild(el("span", "status-block"));
    for (const part of block.split(/(\*\*[^*]+\*\*)/)) {
      if (part === "") continue;
      const bold = part.startsWith("**") && part.endsWith("**");
      const node = bold
        ? document.createElement("strong")
        : document.createTextNode(part);
      if (bold) node.textContent = part.slice(2, -2);
      line.appendChild(node);
    }
  }
}

// --- Loading --------------------------------------------------------------

/**
 * Which model in the repo to read.
 *
 * A named part wins if it exists. A named part that does *not* exist is a
 * miss worth reporting rather than silently substituting: a stale link into a
 * repo whose components were renamed should say so, not quietly open a
 * different model and look like it worked.
 */
/**
 * Is this a GGUF repo? Same rule as `readInventory`'s, and it has to be.
 *
 * This module resolves the component *before* calling `readInventory` — the
 * choice is part of the cache key, so it cannot be made inside the read — which
 * means the format decision is made twice and the two must agree. They agree by
 * asking the same two questions in the same order rather than by one trusting
 * the other.
 */
/**
 * Add each quantization's size and width to a picker already on screen.
 *
 * Runs after the picker renders rather than before, so the choice appears
 * immediately and gains detail a moment later — the alternative is a blank
 * screen while a metadata request completes, which trades a certainty for a
 * nicety.
 *
 * Guards on the state it started from: a visitor who picks or searches again
 * before this lands must not have the previous repo's list rendered over their
 * new one.
 */
async function enrichVariantChoice(
  repo: ResolvedRepo,
  components: readonly RepoComponent[],
): Promise<void> {
  const better = (await variantsWithSizes(repo, components))?.components;
  // The list on screen may have been replaced while the request was in flight —
  // a second load, or the visitor answering the question. Writing then would
  // put one repo's sizes beside another repo's names.
  if (!better || state.componentChoice !== components) return;
  state.componentChoice = better;
  renderView();
}

/**
 * The same components with sizes and bit-widths attached, or `undefined`.
 *
 * Shared by A's full-screen picker and B's dialog, because both ask the same
 * question and "which of these is bigger" is the thing the names cannot answer.
 * One metadata request, no payload.
 *
 * Pure with respect to app state: it returns a list rather than assigning one,
 * so the two callers can each decide whether their own screen is still the one
 * that asked.
 */
interface EnrichedVariants {
  readonly components: readonly RepoComponent[];
  /**
   * Whether `modelVariants` could actually check membership.
   *
   * Gates two things that must not drift apart: whether the picker may state a
   * count and a span, and whether the browser may ask for a repo-level card at
   * all. The generator abstains when it cannot check, and an abstain writes a
   * permanent `failed` record — so asking for a card we know it will refuse
   * burns the key for good rather than merely wasting a message.
   */
  readonly verified: boolean;
  /** The line the card prints. Never computed from an unfiltered set. */
  readonly summary: string | undefined;
  /** What was excluded, with the measurement, so the picker can disclose it. */
  readonly excluded: readonly {
    readonly label: string;
    readonly bitsPerParam: number;
  }[];
}

async function variantsWithSizes(
  repo: ResolvedRepo,
  components: readonly RepoComponent[],
): Promise<EnrichedVariants | undefined> {
  const detail = await fetchVariantSizes(repo.repoId, repo.sha);
  if (!detail) return undefined;

  /*
   * The same exclusion the share card's summary uses, from one definition.
   *
   * A sidecar in this list is counted by the heading, listed as a choice, and
   * counted by the card — and the card and the picker disagreeing about how
   * many quantizations a repo has is exactly the divergence the shared summary
   * function exists to prevent. `modelVariants` needs the sizes and the
   * parameter total, which is why it happens here rather than in discovery.
   *
   * A deep link to an excluded file still resolves: `chooseComponent` matches
   * against the unfiltered `findGgufVariants`, so someone who asked for an MTP
   * head by name still gets it. What changes is only what is offered and what
   * is counted.
   */
  const set = modelVariants(findGgufVariants(repo.files), detail.sizes, detail.params);
  const offered = new Set(set.variants.map((v) => v.key));
  const enriched = components
    .filter((component) => offered.has(component.prefix))
    .map((component) => {
      const variant = set.variants.find((v) => v.key === component.prefix);
      if (!variant) return component;
      const cost = costOf(variant.files, detail.sizes, detail.params);
      if (!cost) return component;
      return {
        ...component,
        bytes: cost.bytes,
        bitsPerWeight: cost.bitsPerWeight,
        fileCount: variant.files.length,
      };
    });

  return {
    components: enriched,
    verified: set.verified,
    /*
     * Built here and handed down, so `renderComponentChoice` has no way to
     * compute one from a list nobody filtered. The rule is that
     * `ggufRepoSummary` never receives an unfiltered set, anywhere, and the
     * cheapest enforcement is to leave the render function unable to call it.
     */
    summary: set.verified
      ? ggufRepoSummary(
          set.variants.map((v) => ({
            label: v.label,
            bytes: costOf(v.files, detail.sizes, undefined)?.bytes,
          })),
        )
      : undefined,
    excluded: set.excluded.map((e) => ({
      label: e.variant.label,
      bitsPerParam: e.bitsPerParam,
    })),
  };
}

/**
 * Enrich the picker, then ask for the repo-level card — in that order.
 *
 * The ask cannot happen at the `ComponentChoiceRequired` catch, where it
 * started: at that moment nothing has been costed, so there is no way to know
 * whether the generator will be able to check membership. Asking anyway means
 * an unparsed repo enqueues, the generator abstains, and §8.3.2 refuses the key
 * for good — a permanent placeholder for a repo that would have worked as soon
 * as the Hub parsed its GGUF. Gated here, the repo simply asks again on the
 * next visit and self-heals.
 *
 * `kind` is no longer the gate. Membership is: only a repo whose variants could
 * be checked asks, which excludes safetensors pipelines by construction because
 * they never reach this path at all.
 *
 * Fire-and-forget like every other card ask — its failure is its own, and the
 * picker is on screen either way.
 */
function enrichAndPrepareCard(
  repo: ResolvedRepo,
  components: readonly RepoComponent[],
): Promise<EnrichedVariants | undefined> {
  return variantsWithSizes(repo, components).then((result) => {
    if (result?.verified) prepareShareCard(repo.repoId, repo.sha);
    return result;
  });
}

/**
 * Open the report dialog for whatever is on screen (REPORT-SPEC §5.1, §6).
 *
 * Reads a **snapshot** and hands it over. The dialog never reads app state
 * again, so a metric change or a tensor click behind it cannot alter what a
 * half-written report claims to be about — and §5.4's isolation runs the other
 * way too: nothing the dialog does touches the map, the URL or the selection.
 *
 * The `part`/`partKey` pair is §6.0's: `componentLabel` is the canonical label
 * that `?part=` carries and the card key is hashed from, and the component
 * itself is the repo's own key — a GGUF filename, or a safetensors prefix
 * ending in a slash. Both, because the label is a function of our code and the
 * key is a fact about the repository.
 */
function openReportFor(opener: HTMLButtonElement): void {
  const inventory = state.inventory;
  const anatomy = state.anatomy;
  if (!inventory || !anatomy) return;
  const repo = inventory.repo;

  openReportDialog(
    {
      repoId: repo.repoId,
      revisionSha: repo.sha,
      context: buildContext({
        format: isGgufRepo(repo) ? "gguf" : "safetensors",
        metric: state.anatomyMetric,
        siteBuild: SITE_BUILD,
        part: componentLabel(repo, inventory.component),
        partKey: inventory.component,
        tower: state.tower,
        selectedTensor: state.selectedTensor,
        /*
         * Only a *completed* comparison (§6). `compareRepo` is set as soon as
         * one resolves, and `compareInventory` only once it has actually been
         * read — so requiring both is what distinguishes "a comparison is on
         * screen" from "a compare pane is open and still loading". Pinning a
         * revision nobody was looking at would put it in the record as evidence.
         */
        comparison:
          state.compareRepo && state.compareInventory
            ? {
                repoId: state.compareRepo.repoId,
                revisionSha: state.compareRepo.sha,
              }
            : undefined,
      }),
    },
    opener,
  );
}

/**
 * The header's part line: which model inside the repo is on screen.
 *
 * `undefined` at the repo root, where there is nothing to disambiguate — a line
 * reading "Part: " on every ordinary model would be noise.
 *
 * It used to carry a "Switch part" link to the same URL without `?part=`, on
 * the argument that such a URL already means "which part was meant?" and needs
 * no new mode. True, and it is also a *document reload*: in compare mode it
 * discarded the comparison before the new part had even been chosen, returning
 * a solo map of a model that had been half a pair a moment earlier (owner,
 * 2026-08-12). The loader bar's part control changes a part in place; this
 * only reports which one.
 */
function partLine(inventory: Inventory): PartLine | undefined {
  const component = inventory.component;
  if (!component) return undefined;
  return {
    label: componentLabel(inventory.repo, component) ?? component,
    switchable: partChoices(inventory) !== undefined,
  };
}

async function load(
  repoIdRaw: string,
  revisionRaw: string,
  selectTensor?: string,
  requestedPart?: string,
): Promise<void> {
  let repoId: string;
  try {
    repoId = normalizeRepoId(repoIdRaw);
  } catch (error) {
    setStatus(messageOf(error), toneFor(error));
    return;
  }
  const revision = revisionRaw.trim() || "main";

  /*
   * Captured before the clears below, so the metric rule can tell a new model
   * from the same one at another precision.
   */
  const previousRepoId = state.inventory?.repo.repoId;

  /*
   * **Pre-flight: resolve and pick the model before anything on screen moves.**
   *
   * This used to happen after `state.loading = true`, so opening a repo that
   * holds several models flashed the loading screen, then replaced it with the
   * welcome screen under a modal — three screens for a question that had not
   * been answered yet, and the middle one was a lie: nothing was being read
   * (owner, 2026-08-12).
   *
   * Two requests are not spent to get this. The resolve was always the first
   * thing the load did; it has only moved earlier than the state changes, and
   * its result is handed to the body rather than fetched again.
   *
   * Nothing is cleared until the answer is known, which also means a repo that
   * turns out to need a choice leaves the model already on screen exactly where
   * it was — the modal has something behind it, which is the rule the two
   * pickers are split on.
   */
  /*
   * Two steps, two catches, and the split is load-bearing.
   *
   * Written as one `try`, the repo was only *maybe* resolved by the time
   * `ComponentChoiceRequired` was caught — so the picker was handed
   * `undefined`, could not look up sizes, and fell back to a bare grid of
   * names. Explore and the part control then showed visibly different dialogs
   * for one question (owner, 2026-08-12).
   *
   * Resolving in its own step makes `repo` **definitely assigned** where the
   * choice is refused, so the size lookup is always available and the compiler
   * says so. The picker's parameter is non-optional for the same reason: this
   * is a mistake that should not be expressible.
   */
  let repo: ResolvedRepo;
  try {
    setStatus(`Resolving ${repoId}@${revision} to a commit…`);
    repo = await resolveRepo(repoId, revision);
  } catch (error) {
    if (error instanceof HubGatedError) {
      setStatus(error.message, "note");
    } else {
      setStatus(messageOf(error), toneFor(error));
    }
    // See the note below: these exits precede the render that would sync it.
    syncPartControls();
    return;
  }

  let chosen: RepoComponent;
  try {
    // Which model in the repo, when it holds several. Resolved before the cache
    // read below because it is part of the cache key: without it, two
    // components of one commit share an entry and the second serves the first
    // one's tensors.
    chosen = chooseComponent(repo, requestedPart);
  } catch (error) {
    if (error instanceof ComponentChoiceRequired) {
      // A question, not a failure — and asked without having disturbed a thing.
      askInitialPart(repoId, revision, error, repo);
      setStatus(
        error.kind === "quantizations"
          ? /*
             * **No count here, and the picker owns it.** This fires at the
             * pre-flight catch, before `fetchVariantSizes` has returned, so
             * `error.components` is the *unfiltered* list — it still holds the
             * imatrix files and sidecars that `modelVariants` removes once the
             * sizes arrive. Printing its length put "offers 28 quantizations"
             * on screen beside a picker and a share card both saying 27
             * (`bartowski/Qwen3.8-27B-GGUF`, 2026-08-20).
             *
             * Updating it asynchronously was the other option and is worse: the
             * number would change under the reader a moment after they read it.
             * The picker is directly below and states the checked figure, so
             * one surface owns the count and this one stops competing.
             *
             * The `models` branch keeps its count — a safetensors pipeline is
             * never filtered, so that number cannot go stale.
             */
            `${repoId} holds one model at several precisions — pick one.`
          : `${repoId} holds ${error.components.length} models — pick one.`,
        "note",
      );
      /*
       * The pre-flight returns before the `finally` that renders, so nothing
       * else will bring the part control up to date — and on a deep link the
       * field was filled programmatically, which fires no `input` event either.
       * Without this the control sat blank beside a repo it could have offered
       * a choice for.
       */
      syncPartControls();
      return;
    }
    setStatus(messageOf(error), toneFor(error));
    syncPartControls();
    return;
  }

  if (loadButton) loadButton.disabled = true;
  loadingSide.a = true;
  /*
   * Set here, beside the other "forget the previous model" clears, so the view
   * cannot render an in-between frame showing the introduction. Cleared in the
   * `finally` below, which is the only exit every path shares — an early
   * `return` on a refusal used to leave the button disabled, and a spinner that
   * never stops would be the same bug with a bigger footprint.
   */
  state.loading = true;
  // Back to the default while the next model loads. Leaving the previous
  // model's name up through a load that then fails would label the tab with a
  // repo the page is no longer showing.
  document.title = LANDING_TITLE;
  state.inventory = undefined;
  // A stale list from the previous repo would render under the new one's name.
  state.componentChoice = undefined;
  state.choiceKind = undefined;
  // A deep link names its tensor up front; loading a new repo from the bar
  // does not. Clearing unconditionally would silently drop the tensor half of
  // every shared link — the URL would rewrite itself to just the repo.
  state.selectedTensor = selectTensor;
  clearAnalysis();
  state.anatomy = undefined;
  state.compareAnatomy = undefined;
  // The opening stack is a property of the repo, not of the session — the same
  // rule `defaultMetric` follows below. Carrying `vision` across a load would
  // open the next model on whichever of its stacks happened to share a name.
  state.tower = undefined;
  renderList();
  renderView();

  try {
    setStatus(`Reading safetensors headers at ${shortSha(repo.sha)}…`);
    // The commit is immutable, so a cached inventory for this exact sha is the
    // same bytes we would re-read — the sha is what makes the entry valid
    // forever rather than merely fresh. A miss or an unusable IndexedDB both
    // land here as `undefined` and cost nothing (see inventory-cache.ts).
    // `repo` and `chosen` come from the pre-flight above, so nothing is
    // resolved twice.

    /*
     * Ask for this model's share card here, before the headers are read
     * (OG-SPEC §8.1).
     *
     * It used to be sent after the whole load finished, which is the obvious
     * reading of "after a model has loaded" and wastes the only overlap
     * available. The browser and the generator do the same work — a shard
     * header each, sequentially, under the same etiquette caps — so on a
     * 96-shard model both take about two minutes. Sent at the end, the visitor
     * waits two minutes and *then* the card starts; sent here, the two run
     * together and the card is usually ready before they have finished looking
     * at the model. Owner observation, 2026-08-09, watching "shard 86 of 96".
     *
     * **Here and not earlier**, though. Two facts have to be true first, and
     * this is the first moment both are: the revision is resolved to a commit,
     * and `chooseComponent` has confirmed the repo actually has safetensors —
     * it throws otherwise. Firing on a bare resolve would enqueue GGUF and
     * PyTorch repos that cannot produce a card, and the cost is not a wasted
     * message: the generator would read the Hub to discover what the browser
     * already knew, spending the one automatic attempt (§4.2) and a slot in a
     * five-worker pool that is the whole bound on our Hub exposure (§14.2).
     *
     * Nothing downstream needs the component: a card is keyed by repo and sha
     * (§10), and the generator picks its own tower.
     *
     * Not awaited, and its failures are its own. Sends `{repoId, sha}` — both
     * already in the address bar — and nothing else.
     */
    /*
     * GGUF included, since 2026-08-10 (plan §19f).
     *
     * The gate was there because the deployed generator predated the GGUF
     * reader, so a GGUF ask would spend the key's one automatic attempt
     * (OG-SPEC §4.2) producing the generic card. That is fixed, and the cost
     * argument the owner withdrew does not replace it: a card is generated once
     * per key and cached immutably for a year, while every visitor reads the
     * same header, so generation is amortised against traffic.
     *
     * The part travels with it, unhashed. Which model in the repo this is
     * changes the map — a GGUF repo's quantizations span 2.89 to 8.53 bits per
     * weight — so without it every variant would share one card. Hashing
     * happens server-side, in `partSegment`, so the browser never needs to
     * know the key's shape.
     */
    prepareShareCard(repo.repoId, repo.sha, componentLabel(repo, chosen.prefix));

    state.componentChoice = undefined;
    const cached = await readCachedInventory(repo.repoId, repo.sha, chosen.prefix);
    const inventory: Inventory = cached
      ? // Splice in the freshly resolved repo: the cached copy carries the
        // `requestedRevision` of whoever populated it, and this load's URL and
        // labels must reflect what *this* visitor asked for. Everything else is
        // a pure function of the sha and so is identical by construction.
        { ...cached, repo }
      : await readInventory(
          repo,
          ({ done, total, bytesRead }) => {
            /*
             * GGUF says bytes; safetensors says shards. Not a cosmetic split —
             * the two formats cost different orders of magnitude for the same
             * thing, and the status line is where that gets disclosed rather
             * than buried. A GGUF index sits behind the tokenizer at a measured
             * median of 10.46 MiB against ~2 KB per safetensors shard, and the
             * owner's decision (plan §4) was to show a running total of real
             * bytes rather than gate on a threshold: no threshold makes a prompt
             * rare when eight of nine files are over 5 MiB, and a forecast came
             * out 66% low when tried.
             *
             * So: nothing to click, and nothing to be wrong about.
             */
            if (bytesRead !== undefined) {
              const mib = (bytesRead / 2 ** 20).toFixed(1);
              const part =
                total > 1 ? ` — part ${Math.min(done + 1, total)} of ${total}` : "";
              setStatus(`Reading GGUF index${part} — ${mib} MiB so far…`);
              return;
            }
            // Sharded frontier models take tens of seconds here. Say so.
            if (total > 1) {
              setStatus(
                `Reading safetensors headers at ${shortSha(repo.sha)} — shard ${Math.min(done + 1, total)} of ${total}…`,
              );
            }
          },
          chosen,
        );
    peeked.a = undefined;
    state.inventory = inventory;
    // Write through on a miss only. Awaited rather than left floating so the
    // order of operations stays predictable; it cannot throw and it cannot
    // stall for long (see the deadline in inventory-cache.ts), so a cache that
    // refuses to be written costs a cold cache, never a failed load.
    if (!cached) await writeCachedInventory(inventory);

    // Anatomy is metadata-only: the headers are already in hand and the config
    // is one small read, so this costs no payload requests and can be the
    // landing view rather than something to opt into.
    const quant = await describeQuantization(repo);
    state.quantization = quant;
    // One stack at a time, defaulting to text — `syncAnatomy` picks the tower
    // and builds the grid from that tower's tensors alone, because a layer
    // index means nothing across stacks. `declared` + `anchors` rather than the
    // bare hint: the config's vocab_size/hidden_size let an embedding or
    // lm_head tensor pin its own bits/group split by measurement instead of by
    // trusting the declaration, which turns most real repos from "product
    // known" into "split known".
    /*
     * Before `syncAnatomy`, because the tower block it renders shows this as
     * the "(of 27.4B)" its own figure is a share of. Computed after `quant` so
     * the fold has the declaration, and once per load rather than per render —
     * see `AppState.modelParams`.
     */
    state.modelParams = modelParamCount(
      inventory.tensors,
      quant?.declared,
      quant?.anchors,
    );
    /*
     * A comparison survives A being re-read.
     *
     * Switching A's quantization used to end compare mode: the picker called
     * `load`, `load` cleared `compareAnatomy`, and nothing put the comparison
     * back — so a click that means "show me this at a different precision"
     * silently answered "and forget what you were comparing it against".
     *
     * Recomputed from the inventory already in hand rather than by re-reading
     * B, which has not changed. **Zero requests**, which is what lets this
     * happen on every part switch without touching the etiquette budget.
     */
    // No `diffInventories` here any more: it ran before `syncAnatomy` had
    // resolved the tower, so it could only ever diff the repos entire.
    syncAnatomy();
    /*
     * The opening metric is a property of the repo, not of the session: a
     * quantized model opens on bits-per-weight, an unquantized one on whichever
     * of dtype or bytes actually varies. Reset per load, so carrying a chip
     * choice across two repos cannot leave the second one flat.
     *
     * **Not across a part switch**, which is the same repo at another precision
     * (owner flow, 2026-08-12). Changing the part is a refinement of the view a
     * visitor is already reading, and resetting the metric under them threw
     * them from bits-per-weight to storage dtype on every click — a chip they
     * had pressed being un-pressed by an action about something else.
     */
    if (state.anatomy && repoId !== previousRepoId) {
      state.anatomyMetric = defaultMetric(state.anatomy);
    }

    /*
     * Named by the format actually read. It said "one safetensors file" for
     * every unsharded repo, which became a plain misstatement the moment GGUF
     * loaded — the status bar asserted safetensors above a map of `Q4_K` cells
     * (seen on a screenshot, 2026-08-09). "Shards" and "parts" are also the
     * words each format's own tooling uses.
     */
    const gguf = isGgufRepo(repo);
    const shardNote = inventory.sharded
      ? `${inventory.files.length} ${gguf ? "parts" : "shards"}`
      : `one ${gguf ? "GGUF" : "safetensors"} file`;
    // Say when nothing was read. A sharded model that took half a minute last
    // time and appears instantly now looks like a bug unless the page accounts
    // for it — and "from cache" is also the honest disclosure that this visit
    // cost the Hub nothing.
    const cacheNote = cached ? " · from cache" : "";
    /*
     * Folded, not `inventory.storedElements`.
     *
     * This line said `4.65B params` for a 27.4B model, and `11.96B` for one
     * called `gpt-oss-20b`, because it summed the header's element counts and a
     * packed `I32` element holds several weights. The map two hundred pixels
     * below was already folding, so the page stated two parameter counts under
     * one name, up to 6.4x apart. See `modelParamCount`, which is the only
     * place this question gets answered now.
     *
     * Whole model rather than the selected stack: this line describes the repo,
     * and a figure that moved when you clicked a tower chip would be a third
     * number with the same name.
     */
    const params = state.modelParams ?? 0;
    setStatus(
      `${inventory.tensors.length} tensors · ${formatParams(params)} params · ${formatBytes(inventory.totalBytes)} · ${shardNote} · pinned at ${shortSha(repo.sha)}${cacheNote}`,
      "ok",
    );
    writeUrlState(repo);
  } catch (error) {
    // A gated repo is not a failure — see `setStatus`. Everything else is.
    if (error instanceof HubGatedError) {
      setStatus(error.message, "note");
    } else if (error instanceof ComponentChoiceRequired) {
      /*
       * A safety net, not the main path: the pre-flight above answers this
       * question before any state moves, so reaching here means the reader
       * refused a component the pre-flight had already accepted. Handled the
       * same way rather than falling through to a generic error, because the
       * repo is still readable and the visitor still just needs to pick.
       */
      askInitialPart(repoId, revision, error, repo);
      setStatus(
        error.kind === "quantizations"
          ? // Countless on purpose — see the pre-flight catch above: at this
            // point the component list is unfiltered, so a number here
            // contradicts the picker's. The picker owns the count.
            `${repoId} holds one model at several precisions — pick one.`
          : `${repoId} holds ${error.components.length} models — pick one.`,
        "note",
      );
    } else {
      setStatus(messageOf(error), toneFor(error));
    }
  } finally {
    /*
     * First, and before any render: every exit lands here — success, refusal,
     * a picker, a thrown error — and each of them owes the reader a screen that
     * is not a spinner. Clearing it anywhere else would leave one path turning
     * forever, and the paths that refuse are the ones nobody exercises.
     */
    state.loading = false;
    loadingSide.a = false;
    syncActionState();
    renderList();
    renderView();
    // A deep link may name a tensor; read it now that the inventory exists.
    if (state.selectedTensor) void selectionChanged();
  }
}

// --- Compare mode ---------------------------------------------------------

/** What A says it came from, or `undefined` before a model is open. */
function provenanceOfA(): Provenance | undefined {
  const inventory = state.inventory;
  if (!inventory) return undefined;
  return readProvenance({
    fileProvenance: inventory.fileProvenance,
    cardData: inventory.repo.cardData,
    repoId: inventory.repo.repoId,
  });
}

/**
 * "Compare with…" — offer what A says it was made from, then open the slot.
 *
 * The suggestion is a dialog rather than a row under the field (owner,
 * 2026-08-12). As a row it occupied three lines of the loader bar permanently,
 * for something read once, on every visit including the ones that never compare
 * anything.
 *
 * **No suggestion, no dialog.** A modal whose only content is a way out is
 * worse than no modal, so a model that declares no parent — every safetensors
 * repo whose card is silent — opens the slot directly and the flow is exactly
 * what it was before this feature existed.
 */
function openCompareWith(): void {
  const provenance = provenanceOfA();
  if (!provenance || provenance.claims.length === 0) {
    setCompareOpen(true);
    compareRepoInput?.focus();
    return;
  }
  openSuggestionDialog({
    provenance,
    opener: compareToggle ?? document.body,
    onPick: (repoId) => {
      setCompareOpen(true);
      if (!compareRepoInput) return;
      compareRepoInput.value = repoId;
      /*
       * Filled, not loaded. The visitor still presses Explore — which is what
       * lets them pin a revision first, or edit the id, and what keeps a
       * suggestion from spending four header reads on a guess they did not
       * confirm.
       */
      compareRepoInput.focus();
      syncActionState();
    },
    onDismiss: () => {
      setCompareOpen(true);
      compareRepoInput?.focus();
    },
  });
}

/** Open or close the second slot. Closing discards B entirely. */
function setCompareOpen(open: boolean): void {
  state.compareOpen = open;
  if (compareSlot) compareSlot.hidden = !open;
  if (compareToggle) compareToggle.setAttribute("aria-expanded", String(open));
  if (slotTagA) slotTagA.hidden = !open;
  if (!open) {
    state.compareRepo = undefined;
    state.compareInventory = undefined;
    state.comparePart = undefined;
    state.compareQuantization = undefined;
    state.diff = undefined;
    state.comparison = undefined;
    state.comparisonError = undefined;
    // Drops B's half of every cell, so the map goes back to being A's alone
    // rather than keeping a column of numbers from a repo no longer on screen.
    syncAnatomy();
    if (state.inventory) writeUrlState(state.inventory.repo);
    renderView();
  }
}

/**
 * Load the second repo and diff it against the first.
 *
 * Costs exactly what loading A cost — a revision resolve, the safetensors
 * headers, and one small config read — and no payload bytes at all. The diff
 * that follows is pure metadata, which is what lets compare mode land on a
 * useful screen before the visitor has clicked a single tensor.
 */
async function loadCompare(
  repoIdRaw: string,
  revisionRaw: string,
  requestedPart?: string,
): Promise<void> {
  const inventory = state.inventory;
  if (!inventory) {
    setStatus(
      "Load a model first, then pick something to compare it against.",
      "error",
    );
    return;
  }

  let repoId: string;
  try {
    repoId = normalizeRepoId(repoIdRaw);
  } catch (error) {
    setStatus(messageOf(error), toneFor(error));
    return;
  }
  const revision = revisionRaw.trim() || "main";

  state.comparison = undefined;
  state.comparisonError = undefined;
  /*
   * Set *and rendered*. Setting alone changed nothing on screen: this function
   * does not render until it finishes, so the control kept showing whatever the
   * last `input` left it — "Choose…" — for the whole read. A flag nobody paints
   * is a flag that does not exist.
   */
  loadingSide.b = true;
  syncPartControls();

  /*
   * Resolved in its own step, exactly as A's loader does — so `repo` is
   * **definitely assigned** where a component choice is refused and the
   * picker's size lookup is always available. See `load`: the version of this
   * that resolved inside the same `try` handed the picker a maybe-undefined
   * repo, and a picker without sizes silently falls back to a bare grid of
   * names.
   */
  let repo: ResolvedRepo;
  try {
    setStatus(`Resolving ${repoId}@${revision} to a commit…`);
    repo = await resolveRepo(repoId, revision);
  } catch (error) {
    setStatus(messageOf(error), toneFor(error));
    loadingSide.b = false;
    syncPartControls();
    return;
  }

  try {
    /*
     * Which model inside B, before a byte of it is read.
     *
     * `readInventory` refuses a multi-model repo rather than guessing, and
     * `loadCompare` used to let that refusal fall through to the generic error
     * handler — so comparing against any GGUF repo reported a failure for a repo
     * that was perfectly readable. Now the refusal opens B's picker, the same
     * one A gets, in a dialog so that answering it does not clear A's map.
     */
    const chosen = chooseComponent(repo, requestedPart);

    setStatus(`Reading ${repo.repoId} headers at ${shortSha(repo.sha)}…`);
    const bInventory = await readInventory(
      repo,
      ({ done, total, bytesRead }) => {
        if (bytesRead !== undefined) {
          setStatus(
            `Reading ${repo.repoId} — GGUF index, ${(bytesRead / 2 ** 20).toFixed(1)} MiB so far…`,
          );
          return;
        }
        if (total > 1) {
          setStatus(
            `Reading ${repo.repoId} — shard ${Math.min(done + 1, total)} of ${total}…`,
          );
        }
      },
      chosen,
    );

    // The inventory supersedes the peek: from here the control reads what is
    // actually open rather than what was typed.
    peeked.b = undefined;
    state.compareRepo = repo;
    state.compareInventory = bInventory;
    state.comparePart = componentLabel(repo, bInventory.component);
    state.compareQuantization = await describeQuantization(repo);
    // B's grid *and* the diff, from the headers and config just read. This is the last thing
    // the compare map needs and it costs nothing further — every value it draws
    // is a function of bytes already in hand, which is why an A-against-B map
    // is free where an A-against-B map of *weights* would be crawling.
    syncAnatomy();

    // `syncAnatomy` sets it whenever B is loaded, which it just was — but the
    // type does not know that, and asserting it away would be the one place a
    // future refactor could drop the diff silently.
    if (state.diff) setStatus(state.diff.verdict, "ok");
    writeUrlState(inventory.repo);
    renderView();

    // A tensor may already be selected — compare it now rather than making the
    // visitor click it again.
    if (state.selectedTensor) void compareSelected();
  } catch (error) {
    /*
     * B holding several models is a question, not a failure — the same
     * distinction A's loader already draws. It fell through to the generic
     * handler before, so comparing against any GGUF repo reported an error for
     * a repo that was entirely readable, and quant-against-quant — the
     * comparison this tool is most useful for — could not be expressed at all.
     *
     * The picker opens in a dialog rather than replacing the view, because A's
     * map is already drawn and answering B's question by clearing it would
     * discard the thing being compared against.
     */
    if (error instanceof ComponentChoiceRequired) {
      askComparePart(repoId, revision, error, repo);
      return;
    }
    setStatus(messageOf(error), toneFor(error));
  } finally {
    /*
     * Every exit, including the early return above — a `finally` runs after a
     * `return` in the `catch`. The control is disabled while this is true, so
     * a path that forgot to clear it would leave the control dead for the
     * rest of the session.
     */
    loadingSide.b = false;
    syncPartControls();
  }
}

/**
 * One title for one question, wherever it is asked from.
 *
 * The same dialog was opened from three places and named itself three ways —
 * "One model, 25 quantizations — owner/repo" from Explore, "Compare against
 * owner/repo" when B needed a part, "Model A — owner/repo" from the control.
 * Same list, same behaviour, three headings, so pressing Explore and pressing
 * the selector looked like two different features (owner, 2026-08-12).
 *
 * The title is the **subject**: which repo is being looked inside. The side is
 * added only when there are two models on screen for it to distinguish — with
 * compare closed, "Model A" names a distinction that does not exist yet. What
 * to do comes from the renderer's own subhead, and how many there are is
 * plain from the list.
 */
function partDialogTitle(repoId: string, side: "a" | "b"): string {
  return state.compareOpen ? `Model ${side.toUpperCase()} — ${repoId}` : repoId;
}

/**
 * Ask which model inside A the visitor meant, before anything is drawn.
 *
 * A modal over the landing screen rather than a page that *is* the question
 * (owner, 2026-08-12). The reviewer's rule for the two chromes was "a modal
 * requires content behind it", and the objection to using one here was that a
 * bare deep link has nothing behind it. It does: the welcome screen. That turns
 * dismissal into a useful answer — someone who opens the list, sees twenty-five
 * quantizations and decides they wanted a different model entirely lands on the
 * screen with the field, their id still in it, rather than on a picker with no
 * way out.
 *
 * Sizes are fetched exactly as B's dialog does: one metadata request, no
 * payload, answering "which of these is bigger" — which the names cannot and
 * which spans a factor of seven within one repo. Its failure is its own; the
 * list is on screen either way.
 */
function askInitialPart(
  repoId: string,
  revision: string,
  choice: ComponentChoiceRequired,
  resolved: ResolvedRepo,
): void {
  openPartDialog({
    title: partDialogTitle(repoId, "a"),
    components: choice.components,
    kind: choice.kind,
    /*
     * The field, not the part control. The control is disabled while no model
     * is loaded — which is exactly when this dialog is open — and a disabled
     * button cannot take focus, so a keyboard user would be returned to
     * nothing. The field is also where the id they typed still is, which is
     * what "changed my mind" wants next.
     */
    opener: repoInput ?? document.body,
    enrich:
      choice.kind === "quantizations"
        ? enrichAndPrepareCard(resolved, choice.components)
        : undefined,
    onPick: (part) => {
      void load(repoId, revision, undefined, part).then(resumeCompare);
    },
    onDismiss: () => {
      setStatus(`Nothing opened. ${repoId} needs one of its models named.`, "note");
    },
  });
}

/**
 * Ask which model inside B, then resume the comparison with that answer.
 *
 * Sizes are fetched for a quantization choice exactly as A's picker does — one
 * metadata request, no payload — because "which of these is bigger" is the
 * question being asked and the names alone cannot answer it.
 */
function askComparePart(
  repoId: string,
  revision: string,
  choice: ComponentChoiceRequired,
  resolved: ResolvedRepo,
): void {
  setStatus(
    choice.kind === "quantizations"
      ? `${repoId} offers ${choice.components.length} quantizations — pick which one to compare against.`
      : `${repoId} holds ${choice.components.length} models — pick which one to compare against.`,
    "note",
  );
  openPartDialog({
    title: partDialogTitle(repoId, "b"),
    components: choice.components,
    kind: choice.kind,
    opener: compareToggle ?? document.body,
    enrich:
      choice.kind === "quantizations"
        ? enrichAndPrepareCard(resolved, choice.components)
        : undefined,
    onPick: (part) => {
      void loadCompare(repoId, revision, part);
    },
    onDismiss: () => {
      setStatus(`No comparison against ${repoId}.`, "note");
    },
  });
}

/**
 * Build one side of a comparison: bytes plus everything needed to read them.
 *
 * `runs` comes from **A's** read plan and is applied to both sides. That is the
 * whole correctness story of compare mode: `planTensorRead` seeds its PRNG from
 * `(sha, tensor)`, so letting each side plan for itself would sample different
 * rows of the two tensors and compare them element-wise — an answer that is
 * confidently wrong and looks entirely reasonable.
 */
async function buildSide(
  repo: ResolvedRepo,
  inventory: Inventory,
  quantization: Awaited<ReturnType<typeof describeQuantization>>,
  tensor: Inventory["tensors"][number],
  runs: readonly RowRun[],
): Promise<CompareSide> {
  const rows = runs.reduce((sum, run) => sum + run.rowCount, 0);

  const group = findAffineGroups(
    inventory.tensors,
    quantization?.declared,
    quantization?.anchors,
  ).groups.find((g) => g.weightName === tensor.name);
  const resolved = group?.resolved;

  if (group && resolved) {
    const scales = inventory.tensors.find((t) => t.name === group.scalesName);
    const biases = inventory.tensors.find((t) => t.name === group.biasesName);
    if (!scales || !biases) {
      throw new Error(
        `${repo.repoId}: ${tensor.name} looks quantized but its scale or bias plane is missing`,
      );
    }
    const triple = await fetchAffineTripleAtRuns(repo, tensor, scales, biases, runs);
    return {
      dtype: tensor.dtype,
      rows: triple.rows,
      cols: resolved.cols,
      affine: {
        scales: triple.scales,
        biases: triple.biases,
        planeDtype: scales.dtype,
        bits: resolved.bits,
        groupSize: resolved.groupSize,
        cols: resolved.cols,
        evidence: group.evidence,
      },
      bytes: triple.packed,
    };
  }

  return {
    dtype: tensor.dtype,
    rows,
    cols: foldTo2D(tensor.shape).cols,
    affine: undefined,
    bytes: await fetchTensorBytesAtRuns(repo, tensor, runs),
  };
}

/**
 * Compare the selected tensor across both repos.
 *
 * Shares `analysisToken` with `analyzeSelected` on purpose: both render into
 * the same pane, so a comparison that finishes after the visitor moved on must
 * be dropped by the same rule that drops a stale analysis.
 */
async function compareSelected(): Promise<void> {
  const aInventory = state.inventory;
  const bInventory = state.compareInventory;
  const bRepo = state.compareRepo;
  const tensorName = state.selectedTensor;
  if (!aInventory || !bInventory || !bRepo || !tensorName) return;

  // Two naming worlds meet here. The tensor list is keyed by *physical* name
  // (`…mlp.down_proj.weight`); the diff table is keyed by *logical* name
  // (`…mlp.down_proj`), because a quantized triple is one tensor with three
  // parts and listing the parts separately is what the folding exists to avoid.
  // A selection can arrive from either, so resolve both ways: the exact
  // inventory name first, so clicking a `.bias` in the list compares that bias
  // rather than its sibling weight, and the diff entry's payload part second.
  const entry = state.diff?.entries.find(
    (e) => e.name === tensorName || e.a?.name === tensorName,
  );
  const aTensor = aInventory.tensors.find((t) => t.name === tensorName) ?? entry?.a;
  const bTensor = bInventory.tensors.find((t) => t.name === tensorName) ?? entry?.b;

  const token = ++analysisToken;
  const isStale = () => token !== analysisToken;
  state.comparisonError = undefined;
  state.comparison = undefined;

  if (!aTensor) return;
  if (!bTensor) {
    state.comparisonError = `${bRepo.repoId} has no tensor named ${tensorName}, so there is nothing to compare it against.`;
    renderView();
    return;
  }

  // Comparability is the diff's judgement, not a fresh guess — one place decides
  // whether two tensors are the same tensor, and the diff table the visitor is
  // looking at must agree with what happens when they click a row.
  if (entry && !entry.comparable) {
    state.comparisonError = `${tensorName} is not comparable across these two repos: ${entry.note}`;
    renderView();
    return;
  }

  renderView();
  setStatus(`Reading ${tensorName} from both repos…`);

  try {
    // A's plan governs. Both sides read the same rows; see `buildSide`.
    const plan = planTensorRead(aTensor, aInventory.repo.sha);

    const a = await buildSide(
      aInventory.repo,
      aInventory,
      state.quantization,
      aTensor,
      plan.rowRuns,
    );
    const b = await buildSide(
      bRepo,
      bInventory,
      state.compareQuantization,
      bTensor,
      plan.rowRuns,
    );

    const result = await analysisClient.compare({
      shaA: aInventory.repo.sha,
      shaB: bRepo.sha,
      tensorName,
      a,
      b,
      complete: plan.mode === "full",
    });
    if (isStale()) return;

    state.comparison = renderTensorComparison({
      tensorName,
      aLabel: `${aInventory.repo.repoId} @ ${shortSha(aInventory.repo.sha)}`,
      bLabel: `${bRepo.repoId} @ ${shortSha(bRepo.sha)}`,
      result,
      sampling: { rowsRead: plan.sampledRows, totalRows: plan.totalRows },
      logScale: state.logScale,
    });
    setStatus(
      `Comparison ready for ${tensorName} — compared ${result.comparison.comparedCount.toLocaleString()} weights in ${result.elapsedMs.toFixed(0)} ms`,
      "ok",
    );
  } catch (error) {
    if (isStale()) return;
    if (error instanceof DOMException && error.name === "AbortError") return;
    state.comparisonError = error instanceof Error ? error.message : String(error);
    setStatus(state.comparisonError, "error");
  } finally {
    if (!isStale()) renderView();
  }
}

/**
 * URL is state (plan §4): the SHA goes in, not the branch name, so a shared link
 * reproduces exactly the bytes that were read — not "whatever main is today".
 * The repo id goes in the path (plan §22); the scheme itself lives in
 * `ui/url-state.ts`.
 *
 * `replaceState`, not `pushState`: clicking twenty tensors must not cost twenty
 * Back presses to escape.
 */
function writeUrlState(repo: ResolvedRepo): void {
  history.replaceState(
    null,
    "",
    buildUrl({
      repoId: repo.repoId,
      rev: repo.sha,
      /*
       * The **label**, which is the same string `prepareShareCard` sends and
       * therefore the same string the card's key is hashed from.
       *
       * This wrote the raw component, and for GGUF that is the variant's *file
       * path* while everything else uses its label — so the URL silently
       * flipped from `?part=Q4_K_M` (what the picker navigates to) to
       * `?part=LFM2.5-2.6B-Q4_K_M.gguf` (what a visitor then copies) once the
       * load finished. The responder hashes whatever the shared URL carries, so
       * it addressed `0bce9c1bc5d4` while the generator had written
       * `42deb74305e5`, and `og.tensorlens.dev` answers a missing card with the
       * fallback PNG at status 200 — a correct card, unreachable, with nothing
       * anywhere reporting a fault.
       *
       * `card-keys.ts` names this exact failure — "one the label, one the
       * filename" — and it happened anyway, because the two producers were four
       * files apart and only one of them was obviously about card keys. The
       * canonical part is the one in the URL, since that is all the responder
       * can ever see.
       *
       * Loading is unaffected either way: `chooseComponent` accepts a variant's
       * key or its label, so links already shared with the file path still
       * resolve the same model.
       *
       * Without the trailing slash: the URL names a directory, and `part=vae/`
       * would round-trip to a different string than the one that produced it.
       * `?.` on `component` too, not only on `inventory`. The schema bump means
       * no stored entry should reach here without it, but this call is on the
       * path every successful load takes, and a field this new is not worth
       * betting a whole page render on.
       */
      part:
        componentLabel(repo, state.inventory?.component)?.replace(/\/$/, "") ||
        undefined,
      tensor: state.selectedTensor,
      // Both sides pinned to their own commit, for the same reason A is: a
      // shared comparison must reproduce the same two sets of bytes later, and
      // a comparison against "whatever main is today" is not reproducible at
      // all — it is the one kind of link where drift on either side silently
      // changes every number on the page.
      compareRepoId: state.compareRepo?.repoId,
      compareRev: state.compareRepo?.sha,
      // Which model inside B, when B holds several. Without it a shared
      // comparison against a GGUF repo reopened as "pick one of 25" — the same
      // state-losing failure `?part=` exists to prevent, on the other side.
      comparePart: state.comparePart,
      view: urlView,
      legacy: false,
    }),
  );

  // The canonical points at the repo, not at this exact view — see
  // `canonicalUrl`. Kept in step with the address bar so a page that has
  // navigated client-side still declares the right one.
  const canonical = document.querySelector<HTMLLinkElement>("#canonical-link");
  if (canonical) canonical.href = canonicalUrl({ repoId: repo.repoId, legacy: false });

  // The tab, for the same reason as the canonical: this page navigated without
  // a document load, so nothing else updates it. See `document-title.ts` for
  // why the model comes first.
  document.title = documentTitle(
    {
      repoId: repo.repoId,
      compareRepoId: state.compareRepo?.repoId,
      component: componentLabel(repo, state.inventory?.component),
      compareComponent: state.comparePart,
    },
    LANDING_TITLE,
  );
}

// --- Rendering ------------------------------------------------------------

/**
 * One row in the tensor list. `aria-current` carries selection, so the state is
 * announced rather than only being visible as a background colour.
 *
 * `tabindex="-1"` is the resting state: exactly one row is promoted to `0` by
 * `setRovingTabindex` once the list is in the DOM, which is what turns a
 * 160-row list from 160 tab stops into one. See `ui/roving-focus.ts`.
 */
/**
 * One row. `prefix` is the group's shared leading substring, which the row does
 * not repeat — the heading above it already carries `model.layers.0.`, and 272
 * rows restating it is what made this column need 400px.
 *
 * The stripped text is *presentation only*: `data-tensor` (identity), `title`
 * (pointer) and `aria-label` (assistive tech) all keep the full name, so a
 * screen-reader user reading rows out of order never loses the layer.
 */
function tensorRowHtml(tensor: Inventory["tensors"][number], prefix: string): string {
  const selected = state.selectedTensor === tensor.name;
  const name = escapeHtml(tensor.name);
  const shown = escapeHtml(
    prefix && tensor.name.startsWith(prefix)
      ? tensor.name.slice(prefix.length)
      : tensor.name,
  );
  return `<button class="tensor-row" type="button" tabindex="-1" data-tensor="${name}" data-label="${shown}" aria-current="${selected}" aria-label="${name}" title="${name}">
    <span class="tensor-row-name">${shown}</span>
    <span class="tensor-row-meta">${escapeHtml(formatShape(tensor.shape))}</span>
  </button>`;
}

/**
 * Joins the parts of the composite keys in this file.
 *
 * NUL cannot occur in a tensor name or a tower label, so no two different pairs
 * of parts can produce the same key.
 *
 * **Written as an escape, never as a literal byte.** A raw NUL in a source file
 * makes `grep` treat the whole file as binary and report *no matches* — not an
 * error, just silence — and this repo has lost time to that six times. `pnpm
 * check-no-nul` is the gate. Same spelling and same reason as `SEPARATOR` in
 * `worker/client.ts` and `cellKey` in `analysis/anatomy.ts`.
 */
const GROUP_SEPARATOR = "\u0000";

/** A run of tensors sharing one layer prefix, in the order the file lists them. */
interface TensorGroup {
  /** Shared leading substring, with its trailing dot. Empty for the non-stack group. */
  readonly prefix: string;
  /** What the heading shows: the prefix without its dot, or `global`. */
  readonly label: string;
  /** Layer index, or `undefined` outside the stack. Orders the groups. */
  readonly layer: number | undefined;
  /** `Tower.id`, or `undefined` on a single-tower model. Orders the groups first. */
  readonly tower: string | undefined;
  readonly tensors: Inventory["tensors"][number][];
}

/**
 * Bucket tensors by layer prefix: the non-stack group first, then layers in
 * index order.
 *
 * **Sorted numerically, not by first appearance.** A safetensors header is a
 * JSON object and its keys arrive in whatever order the writer emitted — for
 * GPT-2 that is lexicographic, which puts `h.10` between `h.1` and `h.2` and
 * makes the layer stack read 0, 1, 10, 11, … A list whose headings are meant to
 * be navigated like a menu has to count in the order the model does.
 *
 * **`global` leads, even though the anatomy map puts it last.** The two are not
 * inconsistent in the way the positions suggest: the map's `global` is a
 * *pinned* column that stays on screen at any scroll offset, so its place at the
 * right costs nothing. A list has no pinning — last means behind every layer in
 * the model, which for a 30-layer checkpoint hides the embedding table, the
 * final norm and the LM head behind 270 rows. It also reads better against the
 * model itself: the embedding is where tokens enter, and it is usually the
 * single largest tensor in the file.
 *
 * File order is preserved *within* a group, where it is the writer's own
 * grouping of a layer's tensors and carries more signal than re-sorting would.
 *
 * **Tower before layer, when there is more than one tower.** A layer index only
 * means something inside its own stack (see `analysis/towers.ts`), so sorting a
 * multimodal checkpoint on the index alone interleaves the stacks — on Gemma 4
 * E4B that reads audio 0, text 0, vision 0, audio 1, text 1, vision 1, … for
 * seventy groups. Sorting by tower first puts each stack's forty-two layers
 * together and turns the list back into a table of contents. On a single-tower
 * model every tensor gets the same `undefined` tower and this is a no-op, which
 * is the point: the common case is untouched.
 */
function groupTensors(
  tensors: readonly Inventory["tensors"][number][],
  towers: readonly Tower[],
): TensorGroup[] {
  const byKey = new Map<string, TensorGroup>();
  const rankOf = new Map(towers.map((tower, index) => [tower.id, index]));

  for (const tensor of tensors) {
    const { prefix, layer } = layerGroupOf(tensor.name);
    const tower = towerOf(tensor.name, towers);
    // Prefix alone is not a key across towers: the non-stack tensors of all
    // three stacks share the empty prefix, which would gather Gemma 4's
    // embedding table, its patch embedder and its audio output projection into
    // one heading reading `global`.
    const key = `${tower ?? ""}${GROUP_SEPARATOR}${prefix}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        prefix,
        label: groupLabel(prefix, tower, towers),
        layer,
        tower,
        tensors: [],
      };
      byKey.set(key, group);
    }
    group.tensors.push(tensor);
  }

  return [...byKey.values()].sort((a, b) => {
    const towerDelta =
      (rankOf.get(a.tower ?? "") ?? 0) - (rankOf.get(b.tower ?? "") ?? 0);
    if (towerDelta !== 0) return towerDelta;
    if (a.layer === undefined) return b.layer === undefined ? 0 : -1;
    if (b.layer === undefined) return 1;
    return a.layer - b.layer;
  });
}

/**
 * The heading over a group.
 *
 * A layer group is named by the file: `model.language_model.layers.7`, which
 * already carries its tower and needs no help. The non-stack group does not —
 * its prefix is empty, so on a multimodal model it needs the tower's name to
 * say which `global` it is. On a single-tower model there is only one, and the
 * bare word it has always used is still right.
 */
function groupLabel(
  prefix: string,
  tower: string | undefined,
  towers: readonly Tower[],
): string {
  if (prefix) return prefix.replace(/\.$/, "");
  const label = towers.find((t) => t.id === tower)?.label;
  return label ? `${label} · global` : "global";
}

/**
 * A group heading plus its rows.
 *
 * A real `<h3>`, so the list is navigable by heading — the pane's `<h2>` names
 * the list and these name the layers under it. `.mono` outranks the display
 * face, which is what these need: the heading is a name from the file, not a
 * title someone wrote.
 */
function tensorGroupHtml(group: TensorGroup): string {
  const rows = group.tensors.map((t) => tensorRowHtml(t, group.prefix)).join("");
  return `<div class="tensor-group">
    <h3 class="tensor-group-head mono">${escapeHtml(group.label)}</h3>
    ${rows}
  </div>`;
}

/**
 * How many rows the list will build at once.
 *
 * A cap, not a semantic limit — it exists so a frontier checkpoint cannot make
 * every filter keystroke rebuild thousands of buttons. Measured in Chrome on
 * this machine: 2076 rows cost 44 ms including layout, 1000 cost 21 ms.
 *
 * 1000 rather than the 500 it started at, because the tower filter has to be
 * able to show a whole tower — that is the one thing it exists to do. Gemma 4
 * E4B's largest is 752 tensors, and under a 500-row cap "audio" would still
 * have opened truncated, which reads as the filter not having worked.
 */
const MAX_ROWS = 1000;

/**
 * Rebuild the tower buttons, but only when the towers themselves changed.
 *
 * Re-emitting the markup on every render would destroy the button the visitor
 * just pressed and drop focus to `<body>` mid-interaction. So the markup is
 * keyed on the tower set and the *pressed* state is written in place, which
 * means clicking a tower leaves focus exactly where it was.
 *
 * Costs no requests: `detectTowers` reads names already in the inventory.
 */
let renderedTowerKey = "";

// Keyed on the inventory object, so a second repo cannot read the first one's
// towers and a re-render of the same repo does not recompute them.
const towerCache = new WeakMap<Inventory, readonly Tower[]>();

function towersFor(inventory: Inventory): readonly Tower[] {
  const cached = towerCache.get(inventory);
  if (cached) return cached;
  const towers = detectTowers(inventory.tensors.map((t) => t.name));
  towerCache.set(inventory, towers);
  return towers;
}

/**
 * The tensors currently in view: one tower's, or the whole inventory when a
 * model has only one stack.
 *
 * The single definition of "what is selected", used by the list, the map and
 * both scope headlines — so the two panes cannot drift into filtering by
 * different rules, which is what "one selection, two views" has to mean in code
 * rather than in a comment.
 */
function tensorsInScope(
  inventory: Inventory,
  towers: readonly Tower[],
  tower: string | undefined,
): readonly Inventory["tensors"][number][] {
  if (tower === undefined) return inventory.tensors;
  return inventory.tensors.filter((t) => towerOf(t.name, towers) === tower);
}

/**
 * Build a grid from one stack's tensors.
 *
 * The filter happens *before* `toLogicalTensors`, which is what makes the whole
 * thing correct rather than merely narrower: a quantized triple's three parts
 * share a name prefix and therefore a tower, so folding after the filter folds
 * exactly the same groups it would have folded before, and every layer index
 * inside the result is unambiguous. Costs no requests — headers and one config
 * read, both already in hand.
 */
function anatomyOf(
  inventory: Inventory,
  quant: Awaited<ReturnType<typeof describeQuantization>>,
  towers: readonly Tower[],
  tower: string | undefined,
): Anatomy {
  return buildAnatomy(
    toLogicalTensors(
      tensorsInScope(inventory, towers, tower),
      quant?.declared,
      quant?.anchors,
    ),
  );
}

/**
 * Keep `state.tower` pointing at a stack this model actually has.
 *
 * Three cases and no fourth: no towers means no selection; an unset or stale
 * selection takes the default; anything else is left alone. A tower id from the
 * previous model would otherwise match nothing and leave an empty list and an
 * empty map with no pressed chip to explain either.
 */
function syncTower(inventory: Inventory): readonly Tower[] {
  const towers = towersFor(inventory);
  if (towers.length === 0) {
    state.tower = undefined;
    return towers;
  }
  if (state.tower === undefined || !towers.some((t) => t.id === state.tower)) {
    /*
     * **A selection names its own tower, and that beats the default.**
     *
     * Reported twice in two days by two visitors (`46988b9f`, `9811b063`), both
     * on multimodal models. A `?tensor=` deep link naming
     * `model.visual.blocks.4.attn.qkv.bias` opened the *text* tower and printed
     * `16.00 bits/weight` beside it — the text stack's figure, captioning a map
     * the linked tensor is not in, with that tensor absent from the list. The
     * reporter flagged `bpw_wrong` and was right: the number was wrong for what
     * they were looking at.
     *
     * The rule (reviewer, 2026-08-23): **a selection event aligns the tower to
     * the tensor; a view event never touches the selection.** It reads as two
     * rules and is one, because every in-session selection is already
     * tower-scoped — the list filters by tower, the map is built from the scoped
     * anatomy, the diff from scoped inventories. Nothing a visitor can *click*
     * selects across towers. The deep link at load is the only producer of a
     * foreign selection, so "the tensor wins" can only fire where there is no
     * browsing context to yank a tower out from under.
     *
     * **Here rather than in `load()`**, because this branch already owns the
     * derived tower and only runs when the tower is unset or stale. A chip click
     * sets a valid tower, so it never reaches this and is never overridden — the
     * in-session half stays untouched by construction rather than by a flag.
     *
     * **Guarded on the tensor existing.** `towerOf` is total: it falls back to
     * the rooted or shared tower for any string, so an unguarded `?tensor=junk`
     * would silently steer which stack the page opens on.
     */
    state.tower = openingTower(state.selectedTensor, towers, (name) =>
      inventory.tensors.some((t) => t.name === name),
    );
  }
  return towers;
}

/**
 * B's grid on the same stack as A's, or the reason there isn't one.
 *
 * Lining the two up is the part that can fail, and it used to fail whenever the
 * two publishers spelled their stacks differently. B was filtered by A's tower
 * **id**, so the answer was only ever as good as the names — and the case this
 * whole feature exists for is the one where the names are exactly what differs:
 * `maglun/Gemma-4-E2B-it-Text-MLX-Mixed-3.62bpw` is `google/gemma-4-E2B-it`'s
 * text tower with `model.layers.N.…` where the parent writes
 * `model.language_model.layers.N.…`. A has no stacks at all, so `tower` is
 * `undefined`, so nothing could match and the comparison declined.
 *
 * Four cases, in order, and the order is the argument:
 *
 *  1. **B has no stacks and A has none either** — nothing to line up, use all of
 *     B. The ordinary case: two quantizations of one decoder-only model.
 *  2. **Both name the same stack** — filter B by it. Exact, free, and right
 *     whenever a quantization keeps its parent's names, which is most of the
 *     time. Tried before any measurement, because an identity is better evidence
 *     than a score.
 *  3. **One side is single-stack and the other is not** — choose the stack by
 *     evidence (`analysis/tower-match.ts`), and decline when the evidence does
 *     not clear its gates. This is the new case and it runs in **both**
 *     directions: the visitor may load the parent first and the extraction
 *     second, and a function that read the argument order would be right one way
 *     round and silently wrong the other.
 *  4. **Anything else** — decline. Two multi-stack models whose labels disagree
 *     need bipartite matching, which is its own milestone.
 *
 * Declining rather than falling back to all of B, in every failing case: that
 * fallback would rebuild the merged grid this whole change exists to delete, and
 * it would do it silently, inside cells a reader is comparing against correct
 * ones.
 *
 * Never issues a request: `state.compareInventory` and
 * `state.compareQuantization` were both fetched when compare mode loaded B, and
 * a cell signature is built from shapes already in hand.
 */
function compareScopeFor(tower: string | undefined): {
  readonly anatomy: Anatomy | undefined;
  readonly declined: string | undefined;
  /** Which of B's stacks was used, when B has stacks. */
  readonly bTower: string | undefined;
  /**
   * Set only when the stack was chosen by measurement rather than named, and
   * carrying **whose** stack it is.
   *
   * The side has to travel with the fit. The scorer is symmetric, so the stack
   * that was identified belongs to whichever model has stacks — which is B when
   * an extraction is compared against its parent, and A when the visitor loads
   * the parent first. Without it the page said "B's text stack" and "A's cells"
   * in both directions, and was exactly backwards in one of them.
   */
  readonly matched: { readonly fit: TowerFit; readonly onSide: "a" | "b" } | undefined;
} {
  const inventory = state.compareInventory;
  const aInventory = state.inventory;
  if (!inventory || !aInventory) {
    return {
      anatomy: undefined,
      declined: undefined,
      bTower: undefined,
      matched: undefined,
    };
  }

  const towers = towersFor(inventory);
  const aTowers = towersFor(aInventory);
  const drawn = (
    bTower: string | undefined,
    matched: { readonly fit: TowerFit; readonly onSide: "a" | "b" } | undefined,
  ) => ({
    anatomy: anatomyOf(inventory, state.compareQuantization, towers, bTower),
    declined: undefined,
    bTower,
    matched,
  });

  // 1 & 2: an identity, which needs no measurement and cannot be wrong.
  if (towers.length === 0 && aTowers.length === 0) return drawn(undefined, undefined);
  if (tower !== undefined && towers.some((t) => t.id === tower)) {
    return drawn(tower, undefined);
  }

  /*
   * 3. One side has stacks and the other does not, so the question is which of
   * that side's stacks the other one *is*.
   *
   * The scorer takes the single-stack side and the multi-stack side, never A
   * and B, so both directions are one call with its arguments in a different
   * order. What differs afterwards is what a match *means*: choosing one of B's
   * stacks decides which tensors B contributes, while choosing one of A's only
   * confirms that the stack already on screen is the one B belongs to — A's
   * grid is the visitor's selection either way and is never re-pointed by a
   * measurement.
   */
  if (towers.length > 0 && aTowers.length === 0) {
    const choice = chooseTower(aInventory.tensors, inventory.tensors);
    const fit = choice.fits[0];
    if (choice.tower && fit) return drawn(choice.tower.id, { fit, onSide: "b" });
    return {
      anatomy: undefined,
      declined: declineReason(choice.declined, "B"),
      bTower: undefined,
      matched: undefined,
    };
  }
  if (aTowers.length > 0 && towers.length === 0) {
    const choice = chooseTower(inventory.tensors, aInventory.tensors);
    const fit = choice.fits[0];
    if (choice.tower && fit && choice.tower.id === tower) {
      return drawn(undefined, { fit, onSide: "a" });
    }
    if (choice.tower) {
      /*
       * B lines up with a stack the visitor is not looking at. Naming the one it
       * does line up with turns a refusal into an instruction — the tower chips
       * are right there — where "cannot be compared" would send them away from a
       * comparison that is one click from working.
       */
      return {
        anatomy: undefined,
        declined: `B lines up with A's ${choice.tower.label} stack, not the ${tower ?? "selected"} one on screen. Switch stacks above to compare them.`,
        bTower: undefined,
        matched: undefined,
      };
    }
    return {
      anatomy: undefined,
      declined: declineReason(choice.declined, "A"),
      bTower: undefined,
      matched: undefined,
    };
  }

  /*
   * 4. **Both multi-stack, labelled differently — one tower against one tower.**
   *
   * Owner's ruling, 2026-08-13: *"the reasonable thing to do is compare one
   * tower with one other tower, not all towers with all towers"*. That deletes
   * the bipartite matching this case was deferred for, and it is the better
   * design rather than the cheaper one — the visitor has already said which
   * stack they are looking at, by pressing a chip, and a whole-model aggregate
   * would answer a question nobody asked while burying the one they did.
   *
   * So it is case 3 with a different single side: score B's stacks against **A's
   * selected stack** rather than against A entire. Same scorer, same gates, same
   * abstention — a checkpoint whose text tower matches two of B's equally well
   * declines exactly as T5 does.
   */
  if (aTowers.length > 0 && towers.length > 0 && tower !== undefined) {
    const choice = chooseTower(
      tensorsInScope(aInventory, aTowers, tower),
      inventory.tensors,
    );
    const fit = choice.fits[0];
    if (choice.tower && fit) return drawn(choice.tower.id, { fit, onSide: "b" });
    return {
      anatomy: undefined,
      declined: declineReason(choice.declined, "B"),
      bTower: undefined,
      matched: undefined,
    };
  }

  // 5. Nothing above applies: A has no stack selected while B has several, so
  // there is no single subject to line B's stacks up against.
  return {
    anatomy: undefined,
    declined:
      "B is a multi-stack model and A has no stack selected, so there is nothing for B's stacks to line up against.",
    bTower: undefined,
    matched: undefined,
  };
}

/**
 * Why the measurement declined, in the reader's terms rather than the scorer's.
 *
 * Two failures with genuinely different meanings, and collapsing them into one
 * sentence would waste the distinction the gates exist to draw: *nothing fits*
 * is a statement about relatedness, and *two things fit equally* is a statement
 * about the evidence being unable to choose — the second is the honest answer
 * for a T5 encoder against its own checkpoint, where a right answer exists and
 * the shapes cannot name it.
 */
function declineReason(
  why: "too-thin" | "ambiguous" | undefined,
  multiSide: "A" | "B",
): string {
  const other = multiSide === "A" ? "B" : "A";
  return why === "ambiguous"
    ? `More than one of ${multiSide}'s stacks fits ${other} equally well, so which one it came from cannot be told from the tensors — and guessing would draw a grid that looks certain.`
    : `No stack of ${multiSide} accounts for enough of ${other} to line the two up, so there is no shared grid to draw.`;
}

/**
 * One tower's slice of an inventory, as an inventory in its own right.
 *
 * **Every derived total is recomputed, and that is the whole point.**
 * `sideTotals` reads `inv.totalBytes` and `dtypeShift` reads `inv.byDtype`, so
 * filtering `tensors` alone would leave a "text tower" inventory whose byte
 * ledger still describes the whole checkpoint — the same page-contradicts-itself
 * defect the scoping exists to remove, wearing the fix's clothes. The one figure
 * deliberately *not* recomputed is `storedElements`, which is the header's own
 * count and is filtered rather than re-derived.
 *
 * Returns the inventory unchanged when there is nothing to scope, so callers do
 * not branch: a single-stack model has no tower and its slice is itself.
 */
function scopedInventory(
  inventory: Inventory,
  towers: readonly Tower[],
  towerId: string | undefined,
): Inventory {
  if (towerId === undefined || towers.length === 0) return inventory;
  const tensors = tensorsInScope(inventory, towers, towerId);
  if (tensors.length === inventory.tensors.length) return inventory;
  return {
    ...inventory,
    tensors,
    byDtype: tallyByDtype(tensors),
    totalBytes: tensors.reduce((sum, t) => sum + t.byteLength, 0),
    storedElements: tensors.reduce((sum, t) => sum + t.numel, 0),
  };
}

/**
 * The stacks B carries that A has none of — one line, not a diff population.
 *
 * When the comparison is scoped to one tower, everything else in the other model
 * leaves the diff entirely, and a reader is owed the fact that it existed.
 * Stating it as a sentence rather than as several hundred "only in B" rows is
 * the reviewer's ruling (2026-08-13): those rows answer *what did I drop*, which
 * is a different question from *how does my extraction differ from the tower it
 * came from*, and mixing them buries the second under the first.
 */
function droppedTowers(
  inventory: Inventory | undefined,
  keptTower: string | undefined,
): readonly string[] {
  if (!inventory || keptTower === undefined) return [];
  return towersFor(inventory)
    .filter((tower) => tower.id !== keptTower && tower.id !== SHARED_LABEL)
    .map((tower) => tower.label);
}

/**
 * Recompute both grids from the current selection.
 *
 * Called wherever the selection can change — a repo loading, B arriving, a
 * tower chip — rather than at each of those sites separately, so the two grids
 * are always built from the same tower by construction. Pure computation over
 * metadata already fetched; it issues nothing and may be called freely.
 */
function syncAnatomy(): void {
  const inventory = state.inventory;
  if (!inventory) {
    state.anatomy = undefined;
    state.compareAnatomy = undefined;
    state.compareDeclined = undefined;
    /*
     * Cleared here too, and not by accident. Every other field this function
     * owns is reset on this path; leaving the two new ones set would carry the
     * previous pair's matched stack into the next comparison, where the note
     * would name a tower belonging to a model no longer on screen. That shape —
     * state that survives a path which skips the recompute — is the defect this
     * arc has already produced three times.
     */
    state.compareTower = undefined;
    state.compareMatch = undefined;
    state.diff = undefined;
    state.droppedTowers = [];
    return;
  }
  const towers = syncTower(inventory);
  state.anatomy = anatomyOf(inventory, state.quantization, towers, state.tower);
  const scope = compareScopeFor(state.tower);
  state.compareAnatomy = scope.anatomy;
  state.compareDeclined = scope.declined;
  state.compareTower = scope.bTower;
  state.compareMatch = scope.matched;

  /*
   * The diff is computed **here**, from the same scope the grid was built from,
   * and that placement is the fix rather than an implementation detail.
   *
   * It used to be computed at load time, before the tower was known, over both
   * repos entire. So one page carried two different B's — a map header saying
   * "600 tensors · 4.65B params" above a totals table saying "2,011 · 5.12B" —
   * and both were honest, which is exactly why it was a trap: a reader holds one
   * mental B. Computing it before `syncAnatomy` also meant switching A's tower
   * left the diff describing the previous one.
   *
   * Costs nothing: `diffInventories` is arithmetic over headers already in hand,
   * which is what makes recomputing it on every tower switch free.
   */
  const compare = state.compareInventory;
  if (!compare) {
    state.diff = undefined;
    state.droppedTowers = [];
    return;
  }
  const bTowers = towersFor(compare);
  state.diff = diffInventories(
    scopedInventory(inventory, towers, state.tower),
    scopedInventory(compare, bTowers, state.compareTower),
  );
  state.droppedTowers = droppedTowers(compare, state.compareTower);
}

/** What the map's scope note states, or `undefined` on a single-stack model. */
/**
 * Always a scope, even when the model has no towers.
 *
 * It used to return `undefined` for a single-tower model, and that one branch
 * decided three unrelated things: whether the statistics line appeared, whether
 * the map's instruction appeared, and whether the tower row appeared. Only the
 * last of those is actually about towers — a model with one stack still has
 * tensors, parameters and a size worth stating, and "click a cell" is true of
 * every map ever drawn. So `gpt2` got none of it (owner, 2026-08-10).
 *
 * Now the scope is always real and `all` carries however many towers there are,
 * including none. What is tower-*dependent* is decided where it is rendered, by
 * looking at `all.length`, rather than here by withholding the whole object.
 */
function towerScopeFor(inventory: Inventory, towerId = state.tower): TowerScope {
  const towers = towersFor(inventory);
  /*
   * `towerId` defaults to A's selection and is passed explicitly for B, because
   * the two can now differ: B's stack is chosen by evidence when the names do
   * not line up, and a stats line reading A's selection would then describe a
   * slice of B that is not the one drawn. It read `state.tower` unconditionally
   * before there was any way for them to disagree.
   */
  const tower = towers.find((t) => t.id === towerId);
  // No towers, or none selected: the scope is the whole model, and `shown`
  // equals the inventory — which is what makes every "(of …)" disappear on its
  // own rather than by a second rule.
  const shown = tower ? tensorsInScope(inventory, towers, tower.id) : inventory.tensors;
  return {
    label: tower?.label ?? "",
    tensors: shown.length,
    totalTensors: inventory.tensors.length,
    bytes: shown.reduce((sum, t) => sum + t.byteLength, 0),
    totalBytes: inventory.totalBytes,
    totalParams: state.modelParams,
    /*
     * Every stack, in detector order, with the mapped one marked — the same
     * shape and the same order the share card's tower line uses.
     *
     * Detector order rather than selected-first, for the card's reason: the
     * order is the chip row's, and reordering by selection would make the line
     * change shape as you click through stacks.
     */
    all: towers.map((t) => ({
      id: t.id,
      label: t.label,
      selected: t.id === tower?.id,
    })),
  };
}

function renderTowerFilter(towers: readonly Tower[]): void {
  if (!towerFilterEl) return;

  const key = towers.map((t) => t.id).join(GROUP_SEPARATOR);
  if (key !== renderedTowerKey) {
    renderedTowerKey = key;
    // `hidden` *and* empty. A hidden element that still holds buttons is one
    // stylesheet away from being operable-but-invisible, and `.tower-filter` is
    // a flex container, whose `display` would otherwise beat `[hidden]` — the
    // rule that restores it lives beside the layout in app.css.
    towerFilterEl.hidden = towers.length === 0;
    // **No "all" chip.** It was the one view that could not be right: with
    // every stack in one grid, `cellKey` merges audio layer 3, text layer 3 and
    // vision layer 3 into a single cell. Keeping it would mean keeping a mode
    // that lies, so the chips are a single-select over real stacks and a model
    // is always showing exactly one. See `defaultTower` for which one it opens
    // on.
    towerFilterEl.innerHTML = towers
      .map(
        (t) =>
          `<button class="chip chip--tower" type="button" data-tower="${escapeHtml(t.id)}" aria-pressed="false">${escapeHtml(t.label)}</button>`,
      )
      .join("");
  }

  for (const button of towerFilterEl.querySelectorAll<HTMLButtonElement>(
    "[data-tower]",
  )) {
    const pressed = (button.dataset.tower ?? "") === (state.tower ?? "");
    button.setAttribute("aria-pressed", String(pressed));
  }
}

// Delegated, and bound once: the buttons are replaced whenever a different
// model loads, and a per-button listener would leak the previous model's.
towerFilterEl.addEventListener("click", (event) => {
  const target = event.target;
  const button =
    target instanceof Element
      ? target.closest<HTMLButtonElement>("[data-tower]")
      : null;
  if (!button) return;
  selectTower(button.dataset.tower || undefined);
});

/**
 * Switch the whole view to one tower.
 *
 * Extracted because there are now two ways to ask for it — the chip row beside
 * the list, and the tower names in the map's own header — and a second copy of
 * this would be a second place for "one selection, two views" to stop being
 * true.
 *
 * One selection, two views: the grid is rebuilt from this tower's tensors and
 * the list is narrowed to the same ones, in that order, from one state change.
 * Two independent filters would be two things to keep in step, and the map's
 * version of "out of step" is a cell whose heading and contents come from
 * different towers.
 *
 * Pure recomputation over metadata already in hand. Changing the tower is
 * presentation: it issues no request, and it must not select a tensor, because
 * selecting one would — see roving-focus.ts for the same rule applied to the
 * arrow keys.
 */
function selectTower(tower: string | undefined): void {
  if (tower === undefined || tower === state.tower) return;
  state.tower = tower;
  syncAnatomy();
  renderList();
  renderView();
}

function renderList(): void {
  if (!listEl) return;
  const inventory = state.inventory;
  if (!inventory) {
    renderTowerFilter([]);
    listEl.innerHTML = `<p class="list-summary">No model loaded.</p>`;
    return;
  }

  // Idempotent, and called here as well as from `syncAnatomy` so the list is
  // never rendered against a stack this model does not have — whichever of the
  // two runs first.
  const towers = syncTower(inventory);
  renderTowerFilter(towers);

  // Read before innerHTML replaces the rows, so "was the visitor standing on a
  // row" is answerable afterwards.
  const focusedTensor =
    listEl.contains(document.activeElement) &&
    document.activeElement instanceof HTMLElement
      ? document.activeElement.closest<HTMLElement>(".tensor-row")?.dataset.tensor
      : undefined;

  const words = state.filter.toLowerCase().split(/\s+/).filter(Boolean);
  // Scope first, then the text filter, and both before the row cap — narrowing
  // to a tower *after* taking the first N of the unfiltered list would show
  // none of it.
  //
  // Through `tensorsInScope` rather than the same predicate written again.
  // That function's comment already claimed to be the single definition used by
  // the list while the list reimplemented it inline; the two agreed exactly,
  // which is what made it a drift point rather than a bug (reviewer, 2026-08-23).
  const matches = tensorsInScope(inventory, towers, state.tower).filter((t) =>
    words.every((w) => t.name.toLowerCase().includes(w)),
  );

  // Grouped first, capped second. Capping the *file* order and grouping what
  // survived is what made Gemma 4 open on layers 0, 1, 2, 10, 11, 12 — a
  // safetensors header lists its keys lexicographically, so `layers.19` comes
  // before `layers.2` and a raw slice keeps an arbitrary subset of the stack.
  // Truncating the ordered groups instead means what is missing is always the
  // tail of the model, which is a thing a reader can reason about.
  let budget = MAX_ROWS;
  const groups: TensorGroup[] = [];
  for (const group of groupTensors(matches, towers)) {
    if (budget <= 0) break;
    groups.push(
      group.tensors.length <= budget
        ? group
        : { ...group, tensors: group.tensors.slice(0, budget) },
    );
    budget -= group.tensors.length;
  }
  const shownCount = groups.reduce((sum, group) => sum + group.tensors.length, 0);
  // "1 tensors" was visible on any single-tensor tower and on a filter that
  // matched once — which, on a page whose whole subject is counting things,
  // reads as a bug in the counting.
  const noun = matches.length === 1 ? "tensor" : "tensors";
  const summary =
    shownCount < matches.length
      ? `${shownCount} of ${matches.length} matching ${noun}`
      : `${matches.length} ${noun}`;

  listEl.innerHTML = `<p class="list-summary">${summary}</p>${groups.map(tensorGroupHtml).join("")}`;

  const rowNodes = [...listEl.querySelectorAll<HTMLButtonElement>(".tensor-row")];
  // The one tab stop lands on the selected row when there is one, so a visitor
  // arriving on a deep link tabs straight to where they already are rather than
  // to the top of a list they have to walk back down.
  const selectedIndex = rowNodes.findIndex(
    (row) => row.dataset.tensor === state.selectedTensor,
  );
  setRovingTabindex(rowNodes, selectedIndex >= 0 ? selectedIndex : 0);

  for (const row of rowNodes) {
    row.addEventListener("click", () => {
      state.selectedTensor = row.dataset.tensor;
      clearAnalysis();
      state.comparison = undefined;
      state.comparisonError = undefined;
      if (state.inventory) writeUrlState(state.inventory.repo);
      renderList();
      renderView();
      void selectionChanged();
    });
  }

  restoreListFocus(rowNodes, focusedTensor);
}

/**
 * Put keyboard focus back where the visitor left it.
 *
 * `innerHTML` destroys the focused element, and a destroyed focus does not fall
 * to its neighbour — it falls to `<body>`, which drops a keyboard user out of a
 * list they were halfway down and back to the top of the document. Every
 * re-render of the rows has this problem: activating a row with Enter
 * re-renders, and so does changing the tower.
 *
 * Three cases, in order:
 *
 *  - the same tensor is still listed — focus its new row, which covers Enter on
 *    a row (the commonest one, and the one where losing focus is worst);
 *  - it was filtered away but rows remain — focus the list's tab stop, so the
 *    visitor lands inside the list they narrowed rather than outside it;
 *  - nothing matched — focus the filter box, which is the only thing left that
 *    can change the answer.
 *
 * Does nothing at all unless focus was already inside the list, so a keystroke
 * in the filter box can never have the list steal it back.
 */
function restoreListFocus(
  rows: readonly HTMLButtonElement[],
  focusedTensor: string | undefined,
): void {
  if (focusedTensor === undefined) return;

  const same = rows.find((row) => row.dataset.tensor === focusedTensor);
  if (same) {
    setRovingTabindex(rows, rows.indexOf(same));
    same.focus({ preventScroll: true });
    return;
  }
  const stop = rows.find((row) => row.getAttribute("tabindex") === "0");
  if (stop) {
    stop.focus({ preventScroll: true });
    return;
  }
  filterInput?.focus({ preventScroll: true });
}

/**
 * The map's *view* state — the part that lives in the DOM rather than in
 * `state`, and so is destroyed every time `renderView` replaces the pane.
 *
 * Selecting a tensor re-renders the whole pane, which rebuilds the map from
 * scratch. Two things were being thrown away with it: how far the layer stack
 * was scrolled, and which cell had keyboard focus. Both matter as soon as the
 * map is something you navigate with — scrolling to layer 24, clicking a cell
 * and landing back at layer 0 is the same interruption as the map jumping down
 * the page, and losing focus to `<body>` ends a keyboard walk after one step.
 */
interface AnatomyViewState {
  readonly scrollLeft: number;
  /** Identity of the focused cell, or `undefined` if focus was elsewhere. */
  readonly focused: { readonly layer: string; readonly role: string } | undefined;
}

function captureAnatomyView(): AnatomyViewState | undefined {
  const scroller = viewEl?.querySelector<HTMLElement>(".anatomy-scroll");
  if (!scroller) return undefined;
  const active = document.activeElement;
  const cell =
    active instanceof Element ? active.closest<SVGRectElement>(".anatomy-cell") : null;
  return {
    scrollLeft: scroller.scrollLeft,
    focused: cell
      ? { layer: cell.dataset.layer ?? "", role: cell.dataset.role ?? "" }
      : undefined,
  };
}

function restoreAnatomyView(previous: AnatomyViewState | undefined): void {
  if (!previous || !viewEl) return;

  const scroller = viewEl.querySelector<HTMLElement>(".anatomy-scroll");
  if (scroller) scroller.scrollLeft = previous.scrollLeft;

  const focused = previous.focused;
  if (!focused) return;
  // Matched by identity rather than by index: `enhanceAnatomyMap` reorders cells
  // into row groups, so position is not stable across a render.
  const cell = [...viewEl.querySelectorAll<SVGRectElement>(".anatomy-cell")].find(
    (candidate) =>
      (candidate.dataset.layer ?? "") === focused.layer &&
      (candidate.dataset.role ?? "") === focused.role,
  );
  // `preventScroll`: the cell is already exactly where it was, and letting focus
  // scroll the page is the jump this is here to remove.
  cell?.focus({ preventScroll: true });
}

/**
 * One example chip, broken across two lines at the slash.
 *
 * A repo id is `owner/name` and the owner half is dead weight for recognition —
 * nobody scans for "mlx-community", they scan for "gemma-4-e2b-it-4bit". Set on
 * one line the three ids are wide enough to wrap the row; split at the slash
 * they sit side by side and the name, the part being read, gets its own line.
 *
 * The slash stays on the first line rather than starting the second, because
 * `owner/` reads as a prefix while `/name` reads as a path fragment.
 *
 * `data-example` keeps the whole unbroken id — the display is split, the value
 * never is.
 */
function exampleChipHtml(repoId: string): string {
  const slash = repoId.indexOf("/");
  const owner = escapeHtml(repoId.slice(0, slash + 1));
  const name = escapeHtml(repoId.slice(slash + 1));
  return `<button class="btn example-chip" type="button" data-example="${escapeHtml(repoId)}"><span class="example-owner">${owner}</span><span class="example-name">${name}</span></button>`;
}

function renderView(): void {
  if (!viewEl) return;
  // Read before anything writes innerHTML, restored after the map is rebuilt.
  const mapView = captureAnatomyView();
  const inventory = state.inventory;

  /*
   * Four states, not two.
   *
   * This was `inventory ? "loaded" : "empty"`, which collapsed three unlike
   * situations into "empty": nothing asked for, a question to answer, and a
   * read in flight. The picker was already one of them — `data-state` said
   * "empty" while a part selector was on screen — and adding the loading screen
   * made the conflation impossible to keep (owner, 2026-08-09).
   *
   * Loading is checked *first* so that searching for a second model shows the
   * spinner rather than dropping the reader back to the introduction, which is
   * the behaviour that mattered: it discarded the model they were looking at
   * and offered example chips to click while a 16 MiB read was in flight.
   */
  /*
   * No "choosing" state any more (owner, 2026-08-12). A repo that holds several
   * models used to replace the whole view with a picker; it now asks in a modal
   * over the landing screen, so cancelling leaves the visitor somewhere — with
   * the model id still in the field — instead of on a page whose only content
   * was the question.
   */
  const appState = state.loading ? "loading" : inventory ? "loaded" : "empty";
  mainEl?.setAttribute("data-state", appState);
  // The loader bar wears it too, so its bottom rule can belong to the loaded
  // screen alone. Set in the same breath as the one above so the two cannot
  // disagree.
  repoForm?.setAttribute("data-state", appState);
  syncPartControls();

  if (appState === "loading") {
    /*
     * Deliberately replaces the whole view, including a loaded model. The
     * alternative — leaving the previous model up — would show numbers for one
     * repo under a field naming another, which is worse than showing nothing.
     */
    viewEl.innerHTML = `<div class="view-loading">Reading model metadata…</div>`;
    return;
  }

  if (!inventory) {
    viewEl.innerHTML = `
      <div class="empty-state">
        <!--
          The illustration and the sticker share a positioning stage.
          .discovery is a centred block with a max-width; without a wrapper the
          sticker would be right-relative to the whole pane and drift as the
          window resizes, instead of staying stuck to the artwork.
        -->
        <div class="discovery-stage">
          ${discoverySvg()}
          ${betaStickerHtml()}
        </div>
        <!--
          The fourth and last copy of this string. The other three are <title>,
          og:title and og:image:alt in index.html, plus HEADLINE in
          og/src/fallback-card.ts — they drifted apart once already, so this one
          is byte-identical to HEADLINE on purpose.

          Headline and subheading are one block, not a heading with a line after
          it: the headline is the hook for someone who has never opened a model,
          the subheading is what the tool actually is for a reader who already
          knows what a tensor is. Neither works alone. See .empty-title in
          app.css for how the subheading is kept narrower than the headline
          without either of them carrying a hand-set width.
        -->
        <div class="empty-title">
          <h2 class="empty-headline">Look inside an AI model</h2>
          <p class="empty-subhead">Explore tensors directly in your browser</p>
        </div>
        ${introHtml()}

        <h3 class="try-it-out">Try it out</h3>
        <div class="example-repos">
          ${EXAMPLE_REPOS.map((r) => exampleChipHtml(r)).join("")}
        </div>
        <p class="prose prose--muted example-hint">
          Explore one of these, or type any public model id in the field above.
        </p>

        ${landingNotesHtml()}
      </div>`;

    for (const button of viewEl.querySelectorAll<HTMLButtonElement>("[data-example]")) {
      button.addEventListener("click", () => {
        const repo = button.dataset.example ?? "";
        if (repoInput) repoInput.value = repo;
        // The load below already passes an empty revision, but the *field* would
        // keep whatever a deep link put there and claim this model was pinned to
        // another model's commit. Same trap as editing the id by hand; see
        // `clearPinnedRevision`.
        clearPinnedRevision(revInput);
        syncActionState();
        void load(repo, "");
      });
    }
    return;
  }

  const selected = inventory.tensors.find((t) => t.name === state.selectedTensor);

  /*
   * While a sweep runs over a tensor already on screen, the results stay and a
   * banner says what is being added. Wrapped rather than replaced: the visitor
   * asked for one more panel, not for the page to forget the answer it gave.
   */
  /*
   * No `role="status"` on the banner, deliberately.
   *
   * The toggle handlers already call `setStatus`, which writes to the page's
   * one live region, and that is the announcement. Making this a second live
   * region would say the same sentence twice to a screen-reader user — the
   * banner is the *visual* half of a message that is already spoken.
   */
  const busyBanner = state.analysisBusy
    ? `<p class="working analysis-busy" aria-hidden="true">${escapeHtml(
        busyMessage({ whatIf: state.whatIf, clipGrid: state.clipGrid }),
      )}</p>`
    : "";

  /*
   * Results show only under the tensor they were computed for.
   *
   * `analysisTensor` exists to answer that, so it may as well answer it here
   * too. The gap it closes is narrow — compare mode's click-through changes the
   * selection without touching `analysis`, so returning to the single view
   * could paint the previous tensor's histogram under the new one's title until
   * the next read finished — but "correct measurement, wrong subject" is the
   * failure this codebase has met five times, and it costs one comparison.
   */
  const shownAnalysis = analysisForSelection(
    state.analysisTensor,
    state.selectedTensor,
    state.analysis,
  );

  const outcomeStyle = state.analysisOutcome
    ? presentOutcome(state.analysisOutcome.kind)
    : undefined;
  const analysisBlock =
    state.analysisOutcome && outcomeStyle
      ? `<div class="notice ${outcomeStyle.noticeClass}"><strong>${escapeHtml(outcomeStyle.heading)}</strong> ${escapeHtml(state.analysisOutcome.message)}</div>`
      : shownAnalysis
        ? busyBanner + shownAnalysis
        : selected
          ? `<div class="card"><p class="working">Reading bytes…</p></div>`
          : "";

  const inCompare = Boolean(state.diff && state.compareRepo);
  const aLabel = `${inventory.repo.repoId} @ ${shortSha(inventory.repo.sha)}`;
  const bLabel = state.compareRepo
    ? `${state.compareRepo.repoId} @ ${shortSha(state.compareRepo.sha)}`
    : "";
  const compareScope: CompareScope | undefined = inCompare
    ? {
        anatomy: state.compareAnatomy,
        aLabel,
        bLabel,
        declined: state.compareDeclined,
        // B's own header figures. `towerScopeFor` reads `state.tower`, which is
        // the tower *both* grids are built on, so the two lines describe the
        // same slice of each model rather than two different questions.
        scope: state.compareInventory
          ? towerScopeFor(state.compareInventory, state.compareTower)
          : undefined,
        matched: state.compareMatch
          ? {
              tower: state.compareMatch.fit.tower.label,
              onSide: state.compareMatch.onSide,
              shared: state.compareMatch.fit.shared,
              of: state.compareMatch.fit.of,
            }
          : undefined,
        part: state.compareInventory ? partLine(state.compareInventory) : undefined,
      }
    : undefined;

  // Anatomy first, drill-down second: with no tensor selected the map *is* the
  // view, and it stays visible underneath once one is.
  const anatomyBlock = state.anatomy
    ? renderAnatomyMap({
        anatomy: state.anatomy,
        metric: state.anatomyMetric,
        repoId: inventory.repo.repoId,
        scope: towerScopeFor(inventory),
        compare: compareScope,
        /*
         * Only ever set for GGUF, because only GGUF has types with no published
         * definition. Computed from the inventory rather than passed down from
         * the reader: the dtype string itself carries the signal (`type42`), so
         * a cached inventory discloses exactly as a freshly read one does.
         */
        formatNotice: describeUnknownTypes(inventory.tensors),
        /*
         * Computed from the inventory rather than carried on the anatomy,
         * exactly as `formatNotice` is: this is a statement about the *file*,
         * and the anatomy is a view of one tower of it. Cheap enough to redo —
         * it is a scan for one suffix.
         */
        exl3Notice: describeExl3(findExl3Groups(inventory.tensors)),
        part: partLine(inventory),
        /*
         * §5.1: the button appears only with a loaded anatomy and a resolved
         * commit. Both hold here by construction — this branch runs when
         * `state.anatomy` exists, and `inventory.repo.sha` is resolved before an
         * inventory is ever built. A cached inventory qualifies for the same
         * reason: it retains the sha it was read at.
         */
        reportEnabled: REPORT_ENABLED,
      })
    : "";

  // **The map leads in compare mode too.**
  //
  // It used to be omitted here, on the argument that the diff took its place —
  // that both answer "what is this model made of" and stacking two full-width
  // grids above the numbers was a waste of the fold. The premise was that a
  // comparison map could not be built without reading every tensor from both
  // repos, which §5 forbids. That premise was wrong: the map's four metrics —
  // storage dtype, bits per weight, bytes, parameters — are all functions of
  // headers already in hand, so an A-against-B map costs **zero** extra Hub
  // requests and there is no budget argument left to make.
  //
  // With that gone, the map stays where it always is. Opening the second slot
  // is a thing you do *while* reading the map, and having it vanish and be
  // replaced by a table is the same interruption as having it move — the map is
  // a place, not a card in a stack.
  if (inCompare && state.diff) {
    const comparisonBlock = state.comparisonError
      ? `<div class="notice notice--warn"><strong>Could not compare that tensor.</strong> ${escapeHtml(state.comparisonError)}</div>`
      : (state.comparison ??
        (selected
          ? `<div class="card"><p class="working">Reading both sides…</p></div>`
          : ""));

    viewEl.innerHTML = `${anatomyBlock}${comparisonBlock}
      ${renderInventoryDiff({
        diff: state.diff,
        aLabel,
        bLabel,
        droppedTowers: state.droppedTowers,
      })}`;

    wireAnatomy();
    restoreAnatomyView(mapView);
    wireCompare();
    // One entry point into the diff's names; arrow keys walk the rest. The
    // handler is on `viewEl` and survives this re-render, but the tabindex
    // attributes do not — they live on buttons that were just replaced.
    setRovingTabindex(
      [...viewEl.querySelectorAll<HTMLElement>("button[data-diff-tensor]")],
      0,
    );
    // Lost in the design merge and restored: without this every explain icon
    // outside the footer is inert markup. The footer is wired once at boot
    // because the shell is built once; everything inside `viewEl` is replaced on
    // each render, so it has to be re-wired on each render.
    wireExplain(viewEl);
    return;
  }

  // The map leads, selected or not.
  //
  // It used to move: with nothing selected the map was the first card, and
  // selecting a tensor inserted the analysis *above* it, pushing the map
  // several cards down the page. So the first click sent the thing you clicked
  // out of view, and every click after that started with scrolling back up to
  // find it. Fixing the order makes the map a place rather than a card in a
  // stack — you pick a cell, the detail appears underneath, and the map has not
  // moved, which is what lets it be used to walk around the model.
  /*
   * Above the tensor card and the analysis, and unconditional where those are
   * not: it explains the labels the reader just clicked on, so it belongs next
   * to the map rather than after the statistics. It also never waits on a
   * payload, so it is the one part of the detail region that is complete the
   * instant a cell is selected.
   */
  const cellTypesBlock =
    state.anatomy && selected && state.inventory
      ? renderCellTypesCard(state.anatomy, selected.name, towersFor(state.inventory))
      : "";
  viewEl.innerHTML = selected
    ? `${anatomyBlock}
       ${cellTypesBlock}
       ${!shownAnalysis && !state.analysisOutcome ? renderTensorCard(selected) : ""}
       ${analysisBlock}
       ${renderByteLedger(inventory)}`
    : `${anatomyBlock}${renderByteLedger(inventory)}`;

  wireAnatomy();
  restoreAnatomyView(mapView);
  wireExplain(viewEl);

  viewEl
    .querySelector<HTMLButtonElement>("#clipgrid-toggle")
    ?.addEventListener("click", () => {
      state.clipGrid = true;
      void analyzeSelected();
    });

  viewEl
    .querySelector<HTMLButtonElement>("#whatif-toggle")
    ?.addEventListener("click", () => {
      state.whatIf = true;
      // Eighteen full passes over the weights — seconds on a large tensor. What
      // gets announced is decided in `analyzeSelected`, which knows whether the
      // pane is being blanked or kept; saying it here too would only be
      // overwritten a line later.
      void analyzeSelected();
    });

  const logToggle = viewEl.querySelector<HTMLButtonElement>("#log-toggle");
  logToggle?.addEventListener("click", () => {
    state.logScale = !state.logScale;
    void analyzeSelected();
  });
}

/**
 * One selection, one destination.
 *
 * Compare mode is not an overlay on the single-tensor view — it answers a
 * different question and renders a different pane — so the choice of which read
 * to perform belongs in one place rather than at each of the three call sites
 * that can change the selection.
 */
function selectionChanged(): Promise<void> {
  return state.compareRepo && state.compareInventory
    ? compareSelected()
    : analyzeSelected();
}

/**
 * Read and analyse the selected tensor.
 *
 * Never called on selection alone in a loop — one tensor, one click, per the
 * no-prefetch rule. The result is cached by (sha, tensor), so flipping back is
 * free and costs the Hub nothing.
 */
/**
 * Put results on screen, recording which tensor they belong to.
 *
 * Paired with `clearAnalysis` so the three fields cannot drift apart: an
 * `analysis` whose `analysisTensor` is stale is the wrong-subject failure with
 * a fresh coat of paint, and a `analysisBusy` left true is a spinner that never
 * stops. Neither is visible in a screenshot.
 */
function setAnalysis(model: TensorViewModel): void {
  state.analysisModel = model;
  state.analysis = renderTensorAnalysis(model);
  state.analysisTensor = model.tensor.name;
  state.analysisBusy = false;
}

/**
 * Take the pane down to nothing, and put the opt-in sweeps back to off.
 *
 * The sweeps are **per tensor**, which is what the card claims — "what each
 * setting would cost *this specific tensor*" — and was not what happened. Both
 * flags were global and never reset, so running the what-if sweep once made
 * every later tensor pay eighteen extra passes without being asked: measured
 * here at 5.5 s to 17.4 s for the same tensor, on every click for the rest of
 * the session. And because the results card replaces the button, there was no
 * longer anything to press, so the sweep could be neither avoided nor
 * re-requested (owner, 2026-08-10).
 *
 * Resetting here rather than at the four call sites: this function already
 * means "a different tensor is the subject now", and the sweeps are a question
 * asked about the old one.
 */
function clearAnalysis(): void {
  state.analysis = undefined;
  state.analysisModel = undefined;
  state.analysisTensor = undefined;
  state.analysisBusy = false;
  state.analysisOutcome = undefined;
  state.whatIf = false;
  state.clipGrid = false;
}

async function analyzeSelected(): Promise<void> {
  const inventory = state.inventory;
  const tensor = inventory?.tensors.find((t) => t.name === state.selectedTensor);
  if (!inventory || !tensor) return;

  const token = ++analysisToken;
  state.analysisOutcome = undefined;
  /*
   * Clear the alert region too, not just the state that feeds the pane.
   *
   * `setStatus` writes an error into a *separate* live region and leaves it
   * there until something else calls `setStatus`. Any path that returns without
   * doing so strands the previous error on screen — which the cached branch
   * below did, so selecting a tensor you had already analysed showed its
   * results under the error from the tensor before it (reported 2026-08-08).
   *
   * Clearing here rather than in that one branch: the bug is "a new selection
   * began and the old error survived", and it would come back with the next
   * early return.
   */
  if (alertEl) alertEl.textContent = "";

  const cached = analysisClient.peek(
    inventory.repo.sha,
    tensor.name,
    state.clipGrid,
    state.whatIf,
  );
  if (cached) {
    // Re-render from cache without touching the network at all.
    const { planTensorRead } = await import("./hf/read-plan.js");
    setAnalysis({
      tensor,
      plan: planTensorRead(tensor, inventory.repo.sha),
      result: cached,
      logScale: state.logScale,
    });
    renderView();
    // Says where it came from: a cached re-render touches no network at all,
    // and silence here is what let a stale error sit under a fresh result.
    setStatus(`Analysis ready for ${tensor.name} — from cache`, "ok");
    return;
  }

  /*
   * Blank the pane only when the *subject* changed.
   *
   * A different tensor invalidates everything on screen, and leaving the old
   * histogram up under a new tensor's title would be the wrong-subject failure
   * this codebase keeps finding. But the what-if and clip-grid toggles do not
   * change the tensor: they ask for an extra panel beside results that are
   * still exactly true. Clearing there discarded a correct histogram for the
   * ten-odd seconds of an eighteen-pass sweep, which read as the page losing
   * the work rather than adding to it (owner, 2026-08-10).
   */
  if (keepsResultsWhileWorking(state.analysisTensor, state.analysis, tensor.name)) {
    state.analysisBusy = true;
    /*
     * Re-render the results we are keeping, in their *running* state. Same
     * numbers, same cards — but the what-if card swaps its button for a
     * spinner, so the card that was asked to do the work is the card that says
     * it is working, and its button cannot be pressed a second time while the
     * first sweep is still going.
     */
    if (state.analysisModel) {
      state.analysis = renderTensorAnalysis({
        ...state.analysisModel,
        // Both, because both sweeps ride on one analyse request: switching on
        // either re-runs the other, so each card says what is true of itself.
        whatIfRunning: state.whatIf,
        clipGridRunning: state.clipGrid,
      });
    }
  } else {
    state.analysis = undefined;
    state.analysisModel = undefined;
    state.analysisTensor = undefined;
  }
  renderView();
  /*
   * The announcement, and the only one — the visual banner is `aria-hidden`
   * precisely because this sentence carries it.
   *
   * Which matters: "the results below are unchanged" is the whole point of
   * keeping the pane up, and a screen-reader user gets that from here or not at
   * all. Told only that a read started, they would have no way to know the
   * numbers under it still stand.
   */
  setStatus(
    state.analysisBusy
      ? busyMessage({ whatIf: state.whatIf, clipGrid: state.clipGrid })
      : `Reading ${tensor.name} …`,
  );

  // Stale means "a newer selection has taken over the screen" — that call will
  // render, so this one must touch neither state nor the DOM.
  const isStale = () => token !== analysisToken;

  try {
    // Is this tensor a quantized triple rather than plain floats? If so its
    // packed integers are not a distribution worth plotting — the reconstructed
    // weights are, and that needs the scale and bias planes alongside.
    const affineGroup = findAffineGroups(
      inventory.tensors,
      state.quantization?.declared,
      state.quantization?.anchors,
    ).groups.find((g) => g.weightName === tensor.name);
    const resolved = affineGroup?.resolved;

    // AWQ/GPTQ, which are not MLX's scheme: the payload is `qweight`, so a
    // click on any other part of the group is not a decode request.
    const packedGroup = tensor.name.endsWith(".qweight")
      ? findPackedIntGroups(inventory.tensors, state.quantization?.declared).find(
          (g) => `${g.prefix}.qweight` === tensor.name,
        )
      : undefined;

    let bytes: ArrayBuffer;
    let plan: Awaited<ReturnType<typeof fetchTensorBytes>>["plan"];
    let rows: number;
    let affine: Parameters<typeof analysisClient.analyze>[0]["affine"];

    let packedRequest: Parameters<typeof analysisClient.analyze>[0]["packed"];

    if (packedGroup) {
      const part = (suffix: string) =>
        inventory.tensors.find((t) => t.name === `${packedGroup.prefix}${suffix}`);
      const qzerosTensor = part(".qzeros");
      const scalesTensor = part(".scales");
      if (!qzerosTensor || !scalesTensor) {
        throw new Error(
          `${tensor.name} looks like a packed quantized group but its zero or scale plane is missing from the inventory`,
        );
      }
      const group = await fetchPackedGroup(
        inventory.repo,
        tensor,
        qzerosTensor,
        scalesTensor,
        part(".g_idx"),
        packedGroup.axis,
        packedGroup.bits,
      );
      bytes = group.payload;
      plan = group.plan;
      rows = group.runs.reduce((n, r) => n + r.features, 0);
      affine = undefined;
      packedRequest = {
        qzeros: group.qzeros,
        scales: group.scales,
        gIdx: group.gIdx,
        planeDtype: scalesTensor.dtype,
        bits: packedGroup.bits,
        groupSize: packedGroup.groupSize,
        axis: packedGroup.axis,
        outFeatures: packedGroup.outFeatures,
        runs: group.runs,
      };
    } else if (affineGroup && resolved) {
      const scalesTensor = inventory.tensors.find(
        (t) => t.name === affineGroup.scalesName,
      );
      const biasesTensor = inventory.tensors.find(
        (t) => t.name === affineGroup.biasesName,
      );
      if (!scalesTensor || !biasesTensor) {
        throw new Error(
          `${tensor.name} looks quantized but its scale or bias plane is missing from the inventory`,
        );
      }
      const triple = await fetchAffineTriple(
        inventory.repo,
        tensor,
        scalesTensor,
        biasesTensor,
      );
      bytes = triple.packed;
      plan = triple.plan;
      rows = triple.rows;
      affine = {
        scales: triple.scales,
        biases: triple.biases,
        planeDtype: scalesTensor.dtype,
        bits: resolved.bits,
        groupSize: resolved.groupSize,
        cols: resolved.cols,
        evidence: affineGroup.evidence,
      };
    } else {
      const plain = await fetchTensorBytes(inventory.repo, tensor);
      bytes = plain.bytes;
      plan = plain.plan;
      rows = plain.plan.sampledRows;
      affine = undefined;
    }

    const result = await analysisClient.analyze({
      sha: inventory.repo.sha,
      tensorName: tensor.name,
      dtype: tensor.dtype,
      rows,
      // Logical width after unpacking, whichever scheme supplied it — the
      // packed word count would misdescribe every row.
      cols: packedRequest
        ? packedRequest.outFeatures
        : affine
          ? affine.cols
          : plan.cols,
      complete: plan.mode === "full",
      includeClipGrid: state.clipGrid,
      includeWhatIf: state.whatIf,
      affine,
      packed: packedRequest,
      bytes,
      /*
       * Patched straight into the one line, not routed through `renderView`.
       *
       * Ticks arrive about ten times a second and `renderView` replaces the
       * whole pane — every chart rebuilt, scroll position lost, focus dropped,
       * ten times a second, to change one number. `isStale` still guards it:
       * the worker is never terminated, so a superseded sweep keeps reporting,
       * and without the check it would drive the progress line of the tensor
       * that replaced it.
       */
      onProgress: (progress) => {
        if (isStale()) return;
        const id =
          progress.phase === "clipgrid" ? CLIP_GRID_PROGRESS_ID : WHAT_IF_PROGRESS_ID;
        const line = document.getElementById(id);
        if (!line) return;
        line.textContent =
          progress.phase === "clipgrid"
            ? clipGridProgressText(progress)
            : whatIfProgressText(progress);
      },
    });
    if (isStale()) return;

    setAnalysis({ tensor, plan, result, logScale: state.logScale });
    // Leads with the fact that the pane is ready, because for a screen-reader
    // user this sentence *is* the pane arriving — nothing else announces it.
    setStatus(
      `Analysis ready for ${tensor.name} — ${plan.mode === "full" ? "read whole" : `sampled ${plan.ranges.length} row runs`} · analysed in ${result.elapsedMs.toFixed(0)} ms`,
      "ok",
    );
  } catch (error) {
    if (isStale()) return;
    // The client raises this when its own newer request superseded ours. Same
    // meaning as a stale token, and equally not something to show anyone.
    if (error instanceof DOMException && error.name === "AbortError") return;

    const raw = error instanceof Error ? error.message : String(error);
    // A refusal to decode is a teachable moment, not just a failure: read the
    // repo's own config and name the scheme. Only on the error path, so it costs
    // the Hub nothing in the common case.
    if (raw.startsWith("Cannot decode dtype ")) {
      // A sidecar of a packed group is not an unsupported scheme — it is a
      // plane with nothing to plot, and saying otherwise sends a reader away
      // from a tensor the tool handles perfectly well two clicks over.
      const quantization = await describeQuantization(inventory.repo);
      state.analysisOutcome = {
        kind: "unsupported",
        message:
          describePackedSidecar(
            tensor.name,
            findPackedIntGroups(inventory.tensors, quantization?.declared),
          ) ??
          // The tensor's own bytes, so the sentence can check the type table
          // against the file rather than quoting it. A colliding id otherwise
          // prints a width the header two lines up contradicts — `ffcf61ac`.
          explainUndecodable(tensor.dtype, quantization, {
            numel: tensor.numel,
            byteLength: tensor.byteLength,
          }),
      };
    } else {
      state.analysisOutcome = { kind: "error", message: raw };
    }
    if (isStale()) return;
    /*
     * Tone from the same function the pane's chrome comes from, so the banner
     * and the announcement cannot disagree. See `ui/analysis-outcome.ts` — the
     * screen-reader path is the half nobody looks at, and it is exactly the half
     * that silently kept saying "Error:".
     */
    setStatus(
      state.analysisOutcome.message,
      presentOutcome(state.analysisOutcome.kind).tone,
    );
  } finally {
    /*
     * Always re-render for the *current* selection. The previous version
     * returned early from the stale checks without rendering, which left the
     * pane stuck on "Reading bytes…" and the status frozen mid-sentence
     * whenever two reads overlapped — a hang with no error anywhere, which is
     * the worst way for this to fail.
     *
     * `analysisBusy` is cleared here rather than on each exit for the same
     * reason: every path out of the try, including the error one and a throw
     * nobody anticipated, has to stop the spinner. A stale one is a page that
     * looks like it is still working forever.
     */
    if (!isStale()) {
      state.analysisBusy = false;
      renderView();
    }
  }
}

/**
 * Listeners for the compare view: the log-scale toggle, and click-through from a
 * diff row to that tensor's comparison.
 */
function wireCompare(): void {
  if (!viewEl) return;

  viewEl
    .querySelector<HTMLButtonElement>("#compare-log-toggle")
    ?.addEventListener("click", () => {
      state.logScale = !state.logScale;
      void compareSelected();
    });

  for (const row of viewEl.querySelectorAll<HTMLElement>("[data-diff-tensor]")) {
    row.addEventListener("click", () => {
      const name = row.dataset.diffTensor;
      if (!name) return;
      state.selectedTensor = name;
      state.comparison = undefined;
      state.comparisonError = undefined;
      if (state.inventory) writeUrlState(state.inventory.repo);
      renderList();
      renderView();
      void compareSelected();
    });
  }
}

/**
 * Hover/focus detail and metric switching for the map.
 *
 * Detail goes to a fixed panel rather than a floating tooltip: the grid is
 * horizontally scrollable and can be wider than the viewport, and a tooltip
 * anchored to a cell near the right edge would be clipped or would itself
 * trigger a scroll.
 */
function wireAnatomy(): void {
  if (!viewEl) return;
  // Roles, rows and the one-tab-stop keyboard contract first: the listeners
  // below are bound to the cells, and `enhanceAnatomyMap` moves them into row
  // groups. Binding first would still work — moving a node keeps its listeners
  // — but reading it in the order the DOM ends up in is worth more than that.
  enhanceAnatomyMap(viewEl);
  const tip = viewEl.querySelector<HTMLDivElement>("#anatomy-tip");
  const anatomy = state.anatomy;

  // Present only in compare mode, and then possibly `undefined` because B's
  // stacks could not be lined up with A's — in which case the panel is A's
  // alone, exactly as the grid is. See `compareScopeFor`.
  const compare = state.compareAnatomy;

  for (const node of viewEl.querySelectorAll<SVGRectElement>(".anatomy-cell")) {
    const show = () => {
      if (!anatomy || !tip) return;
      const layerAttr = node.dataset.layer;
      const layer =
        layerAttr === "" || layerAttr === undefined ? undefined : Number(layerAttr);
      const key = cellKey(layer, node.dataset.role ?? "");
      const cell = anatomy.cells.get(key);
      // `null` is "compare mode, B has nothing here" and `undefined` is "not
      // comparing" — two different panels, so the two absences stay distinct
      // all the way from the grid to the words a reader gets.
      if (cell) {
        tip.innerHTML = compare
          ? cellDetail(cell, compare.cells.get(key) ?? null)
          : cellDetail(cell);
      }
    };
    node.addEventListener("mouseenter", show);
    node.addEventListener("focus", show);
    // Click-through to the tensor view — one tensor, one click, no prefetch.
    node.addEventListener("click", () => {
      if (!anatomy) return;
      const layerAttr = node.dataset.layer;
      const layer =
        layerAttr === "" || layerAttr === undefined ? undefined : Number(layerAttr);
      const cell = anatomy.cells.get(cellKey(layer, node.dataset.role ?? ""));
      // The payload, not `parts[0]` — see `LogicalTensor.payloadName`.
      const payload = cell?.tensors[0]?.payloadName;
      if (!payload) return;
      state.selectedTensor = payload;
      clearAnalysis();
      state.comparison = undefined;
      state.comparisonError = undefined;
      if (state.inventory) writeUrlState(state.inventory.repo);
      renderList();
      renderView();
      void selectionChanged();
    });
  }

  /*
   * The report button, bound per render like the metric chips beside it,
   * because `renderView` replaces the row.
   *
   * Behind the build-time flag as well as the markup being absent without it —
   * belt and braces that costs nothing, and it means this loop is dead code
   * Rollup can drop along with the dialog import.
   */
  if (REPORT_ENABLED) {
    for (const button of viewEl.querySelectorAll<HTMLButtonElement>(
      "[data-report-open]",
    )) {
      button.addEventListener("click", () => openReportFor(button));
    }
  }

  /*
   * The share button, bound per render like the chips beside it.
   *
   * Not behind a build-time flag, unlike Report: that flag exists because the
   * report endpoint accepts prose from strangers and shipping the dialog before
   * the inbox was proven would have been a public write path with nobody
   * reading it. Sharing writes a path this page already displays, so there is
   * nothing to stage.
   */
  for (const button of viewEl.querySelectorAll<HTMLButtonElement>(
    "[data-share-open]",
  )) {
    button.addEventListener("click", () => {
      /*
       * Read back from the address bar rather than reassembled from app state.
       *
       * `writeUrlState` already put the canonical URL there — including the
       * `part` **label** rather than the filename, a distinction that has cost
       * this project a whole class of unreachable cards. Rebuilding the state
       * here would be a second producer of the same string, four files from the
       * first, which is exactly how that happened.
       *
       * It is also what a visitor would get by copying the address bar, so the
       * short link and the manual copy cannot describe different views.
       */
      openShareDialog(parseUrlState(location.pathname, location.search), button);
    });
  }

  for (const chip of viewEl.querySelectorAll<HTMLButtonElement>("[data-metric]")) {
    chip.addEventListener("click", () => {
      state.anatomyMetric = (chip.dataset.metric ?? "bpw") as AnatomyMetric;
      renderView();
    });
  }

  // The part values in the map's header, which open the same modal the loader
  // bar's control does. Bound per render like the metric chips, because
  // `renderView` replaces them.
  for (const button of viewEl.querySelectorAll<HTMLButtonElement>("[data-part-open]")) {
    button.addEventListener(
      "click",
      () => void openPartChooser(button.dataset.partOpen === "b" ? "b" : "a"),
    );
  }

  // The unselected tower names in the map's header, and the "show the other
  // tower" offer in the mismatch disclosure. Bound per render like the metric
  // chips, because `renderView` replaces them — and through the same
  // `selectTower` the chip row uses, so no two controls can disagree about what
  // switching means. The disclosure reuses this attribute rather than adding a
  // third path, which is the whole reason it is an attribute and not a handler.
  for (const name of viewEl.querySelectorAll<HTMLButtonElement>(
    "[data-tower-switch]",
  )) {
    name.addEventListener("click", () => selectTower(name.dataset.towerSwitch));
  }

  wireAnatomyScrollEdges();
}

/**
 * The fades that say which way the layer axis continues.
 *
 * The rules between the pinned bands and the scroller are CSS and are always
 * there; *which* edge has more behind it is a measurement, so it lives here.
 * Kept out of the render string on purpose: a fade baked into markup would
 * claim there is more to see on a model that fits, and "it fits" is only known
 * once the box has a width.
 */
let anatomyScrollResize: ResizeObserver | undefined;

function wireAnatomyScrollEdges(): void {
  const scroller = viewEl?.querySelector<HTMLDivElement>(".anatomy-scroll");
  const wrap = scroller?.parentElement;
  // Disconnect first: `renderView` replaces the pane on every chip click, and an
  // observer still holding the old, detached scroller would keep it alive and
  // accumulate one more on each render.
  anatomyScrollResize?.disconnect();
  anatomyScrollResize = undefined;
  if (!scroller || !wrap) return;

  const sync = () => {
    const max = scroller.scrollWidth - scroller.clientWidth;
    // A pixel of tolerance: fractional layout widths otherwise leave a fade lit
    // at an edge that is already as far as it goes.
    wrap.classList.toggle("is-clipped-start", scroller.scrollLeft > 1);
    wrap.classList.toggle("is-clipped-end", scroller.scrollLeft < max - 1);
  };
  scroller.addEventListener("scroll", sync, { passive: true });
  // Resizing the window changes what overflows without firing a scroll event.
  anatomyScrollResize = new ResizeObserver(sync);
  anatomyScrollResize.observe(scroller);
  sync();
}

/**
 * What the selected cell's type labels mean, as a card under the map.
 *
 * The owner's shape (2026-08-19): clicking a cell already produces a region of
 * detail beneath the map — the tensor card, then the analysis once the weights
 * are read — and the type explanations belong in it rather than in the hover
 * panel, where their variable height moved the page under the cursor.
 *
 * **Rendered from the header alone, and deliberately before the payload.** It
 * sits beside a card that waits on a multi-megabyte read, so it would be easy to
 * fold it in there; it must not be. Learning what `IQ2_XXS` is cannot be a thing
 * a reader downloads 20MB for, and this needs nothing the map did not already
 * have.
 *
 * **Per cell, not per tensor.** The tensor card below it answers "this tensor";
 * this answers "this cell", which is the thing that was clicked and the only
 * scope at which a folded group's mixed schemes exist to be explained at all.
 */
function renderCellTypesCard(
  anatomy: Anatomy,
  selectedTensor: string,
  towers: readonly Tower[],
): string {
  const cell = [...anatomy.cells.values()].find((c) =>
    c.tensors.some(
      (t) => t.payloadName === selectedTensor || t.name === selectedTensor,
    ),
  );
  /*
   * **The selected tensor is not in the drawn tower — say so rather than
   * degrade.**
   *
   * This returned `""` and lost the card silently, which is the third surface to
   * go quiet in that state: the list does not contain the tensor either, and the
   * map has no cell to highlight. A reader saw a tensor pane, a map, and figures
   * that all described different things, with nothing naming the discrepancy.
   * That is what `46988b9f` reported as `bpw_wrong`.
   *
   * A tower change is a *view* event and must never move the selection
   * (`selectTower` above, and the `tower` field's own contract), so the honest
   * resolution is disclosure, not reconciliation — the same posture as
   * `ComponentChoiceRequired` and the unknown-type note. The offer to switch
   * goes through `selectTower`, the one switching path the header's tower names
   * already use, so a second control cannot disagree about what switching means.
   *
   * Deep links cannot reach this any more — `syncTower` now opens on the
   * tensor's own tower — so this is the in-session case only: selected a tensor,
   * then switched tower to look at another stack.
   */
  if (!cell) {
    const home = towerOf(selectedTensor, towers);
    if (!home || home === state.tower) return "";
    return `
      <section class="card cell-types" aria-label="Selected tensor is in another tower">
        <p class="card-title">Showing a different tower</p>
        <p class="type-note">
          <code class="mono">${escapeHtml(selectedTensor)}</code> is in the
          <strong>${escapeHtml(home)}</strong> tower; the map above shows
          <strong>${escapeHtml(state.tower ?? "")}</strong>, and every figure
          beside it describes that stack.
        </p>
        <p class="cell-where">
          <button type="button" class="btn" data-tower-switch="${escapeHtml(home)}">
            Show the ${escapeHtml(home)} tower
          </button>
        </p>
      </section>`;
  }
  const body = cellTypeNotes([cell]);
  // Nothing to say: a plain F32 tensor with no scheme and no described dtype.
  // An empty card is worse than none — it reads as content that failed to load.
  if (!body) return "";
  const where =
    cell.layer === undefined ? cell.role : `layer ${cell.layer} · ${cell.role}`;
  return `
    <div class="card">
      ${cardTitle(`What this cell stores <span class="cell-where mono">${escapeHtml(where)}</span>`)}
      <div class="cell-types">${body}</div>
    </div>`;
}

function renderTensorCard(tensor: Inventory["tensors"][number]): string {
  return `
    <div class="card">
      <h2 class="mono tensor-title">${escapeHtml(tensor.name)}</h2>
      <dl class="stat-grid">
        ${stat("shape", formatShape(tensor.shape))}
        ${stat("dtype", tensor.dtype)}
        ${stat("params", formatParams(tensor.numel))}
        ${stat("bytes", formatBytes(tensor.byteLength))}
        ${stat("file", tensor.file)}
        ${stat("byte range", `${tensor.absoluteStart.toLocaleString()} – ${tensor.absoluteEnd.toLocaleString()}`)}
      </dl>

    </div>`;
}

function stat(label: string, value: string): string {
  // Styling lives in `.stat-grid dt/dd` in app.css — these were inline copies of
  // those rules, which meant the two drifted apart the moment the type scale
  // moved. The grid container carries the class; these just fill it.
  return `<div>
    <dt>${escapeHtml(label)}</dt>
    <dd class="mono">${escapeHtml(value)}</dd>
  </div>`;
}

/**
 * The bytes ledger: where a repo's weight bytes actually live, by dtype. Cheap
 * (it is a group-by over data we already have) and clarifying out of proportion
 * to its cost — it is how a "4-bit" repo reveals how much of itself is f16.
 */
/**
 * The explain icon for one storage type, or nothing.
 *
 * Nothing, rather than a placeholder, when the type has no copy: a dtype string
 * is whatever the file's header said, and the header parser lets unknown ones
 * through on purpose. An icon that opens onto "no description available" is a
 * control that costs a reader a click to learn nothing.
 */
function dtypeExplain(dtype: string): string {
  const entry = explainForDtype(dtype);
  return entry ? explainIconFor(entry, dtype) : "";
}

function renderByteLedger(inventory: Inventory): string {
  const rows = inventory.byDtype
    .map(
      (tally) => `<tr>
        <td class="mono">${escapeHtml(tally.dtype)}${dtypeExplain(tally.dtype)}</td>
        <td class="mono" style="text-align:right">${tally.tensors}</td>
        <td class="mono" style="text-align:right">${formatParams(tally.storedElements)}</td>
        <td class="mono" style="text-align:right">${formatBytes(tally.bytes)}</td>
        <td class="mono" style="text-align:right">${dtypeBitsPerWeight(tally)}</td>
        <td class="mono" style="text-align:right">${((tally.bytes / inventory.totalBytes) * 100).toFixed(1)}%</td>
      </tr>`,
    )
    .join("");

  /*
   * "in the model", because this card is the one thing on the page that is not
   * about the selected tensor.
   *
   * It sits directly under the analysis pane, so with a tensor open the reader
   * has just been looking at one tensor's numbers and arrives at a table with
   * the same shape and a much larger total (owner, 2026-08-10). "The model" is
   * exact: `inventory` is one component of one repo — a GGUF variant with every
   * split part, or a safetensors component with every shard — which is the
   * thing the page is showing, not the repo, which may hold several.
   */
  return `
    <div class="card">
      ${cardTitle("Where the bytes live in the model")}
      <table style="width:100%;border-collapse:collapse;font-size:12.5px">
        <thead>
          <tr style="color:var(--muted);text-align:left">
            <th style="font-weight:600;padding-bottom:6px">dtype${explainIcon("dtype")}</th>
            <th style="font-weight:600;text-align:right">tensors${explainIcon("tensor-count")}</th>
            <th style="font-weight:600;text-align:right">elements${explainIcon("params")}</th>
            <th style="font-weight:600;text-align:right">bytes${explainIcon("bytes")}</th>
            <th style="font-weight:600;text-align:right">bpw${explainIcon("dtype-bpw")}</th>
            <th style="font-weight:600;text-align:right">share${explainIcon("byte-share")}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

/**
 * One dtype's measured bits per weight: its bytes over its parameters.
 *
 * Measured rather than looked up, which is the whole value of the column. A
 * block format's name is not its width — `Q4_K` is four-bit quants that land
 * near 4.5 once each super-block's scales and mins are counted, and `IQ2_XXS`
 * near 2.06 — and this arithmetic is the only place on the page those show up
 * as what they cost rather than as what they are called.
 *
 * Two decimals because that is where the formats separate: `Q4_0` and `Q4_K`
 * are both "4-bit" and differ by 0.00 at one decimal on some tensors.
 */
function dtypeBitsPerWeight(tally: {
  readonly storedElements: number;
  readonly bytes: number;
}): string {
  // A dtype with no parameters is not a division worth doing. It should not
  // occur — every tally comes from tensors that have a shape — but an em dash
  // is the honest output if it ever does, rather than `Infinity` or `NaN`.
  if (tally.storedElements <= 0) return "—";
  return ((tally.bytes * 8) / tally.storedElements).toFixed(2);
}

// --- Wiring ---------------------------------------------------------------

repoForm.addEventListener("submit", (event) => {
  event.preventDefault();
  // A submit reads whatever the form holds. When the second slot is open and
  // filled that means both sides, in order — B's diff needs A's inventory, so
  // they cannot be issued together, and doing so would also put four header
  // reads in flight against two repos at once.
  void load(repoInput.value, revInput.value, undefined, submittedPart()).then(() => {
    if (state.compareOpen && compareRepoInput.value.trim() && state.inventory) {
      // `comparePart` may have been chosen from the bar before this submit —
      // see `openPartChooser`. Without it the picker would ask again for an
      // answer already given.
      void loadCompare(
        compareRepoInput.value,
        compareRevInput.value,
        state.comparePart,
      );
    }
  });
});

/**
 * Re-issue the comparison after A has been (re-)read, if there is one to make.
 *
 * The submit handler has always done this; the picker did not, so answering
 * "which quantization?" ended compare mode — and on the deep-link path it did
 * so *silently*, because the page had arrived with B named in the URL and the
 * fields already filled. The visitor saw B in the form and no comparison on
 * screen, which is the worst of both.
 *
 * Guarded on `state.compareInventory` being absent: when B is already in hand
 * `load` recomputes the diff itself for free, and re-reading B would spend four
 * header requests to arrive at the bytes it is already holding.
 */
function resumeCompare(): void {
  if (!compareRepoInput || !compareRevInput) return;
  if (!state.compareOpen || !state.inventory) return;
  if (state.compareInventory) return;
  const repoId = compareRepoInput.value.trim();
  if (repoId) void loadCompare(repoId, compareRevInput.value, state.comparePart);
}

/**
 * The parts each side could be showing, or `undefined` when there is no choice.
 *
 * A repo offering exactly one model has nothing to pick, and a control that
 * opens a list of one is a question with a forced answer.
 */
interface PartChoices {
  readonly components: readonly RepoComponent[];
  readonly kind: "models" | "quantizations";
}

function partChoices(inventory: Inventory | undefined): PartChoices | undefined {
  return inventory ? partChoicesOf(inventory.repo) : undefined;
}

/**
 * What a repo offers, from its file list alone — no inventory needed.
 *
 * Split out so the part control can answer "is there a choice here?" for a repo
 * that has been *typed* but not yet opened. See `peekParts`.
 */
function partChoicesOf(repo: ResolvedRepo): PartChoices | undefined {
  if (isGgufRepo(repo)) {
    const variants = findGgufVariants(repo.files);
    return variants.length > 1
      ? { components: variants.map(variantAsComponent), kind: "quantizations" }
      : undefined;
  }
  const components = findComponents(repo.files);
  return components.length > 1 ? { components, kind: "models" } : undefined;
}

/**
 * A repo named in the bar but not yet opened, and what it offers.
 *
 * The part control was disabled until its side had *loaded*, so someone who
 * typed a GGUF repo into B saw a control that looked like the answer to
 * "which quantization?" and did nothing when clicked — the choice only appeared
 * after pressing Explore, which is a step later than the question occurs to you
 * (owner, 2026-08-12).
 */
interface PartPeek {
  readonly repoId: string;
  readonly repo: ResolvedRepo;
  readonly choices: PartChoices;
  /** The part picked before loading, when one has been. */
  chosen?: string;
}

const peeked: { a?: PartPeek; b?: PartPeek } = {};

/**
 * A read is in flight for this side.
 *
 * The part control asks two different questions depending on whether its side
 * is open: a loaded side is answered by its inventory, a merely typed one by
 * the syntax of the field. **Mid-load it is neither** — the inventory is not
 * there yet and the syntax says nothing about what the repo holds — so the
 * syntactic branch took over and offered "Choose…" over a model that was
 * seconds from proving it has nothing to choose (owner, 2026-08-12).
 *
 * Not in `AppState` because it is about the form, not the view, and because
 * `state.loading` already means something narrower: A's load, which drives the
 * whole-page loading screen.
 */
const loadingSide: { a: boolean; b: boolean } = { a: false, b: false };

/**
 * Repo ids already found to hold exactly one model, per side.
 *
 * So the control settles back to unavailable after the one click that asked,
 * rather than staying live and answering "nothing to choose" every time.
 */
const singlePart: { a?: string; b?: string } = {};

/** `normalizeRepoId`, without throwing on the half-typed. */
function normalizedOrUndefined(raw: string): string | undefined {
  try {
    return normalizeRepoId(raw);
  } catch {
    return undefined;
  }
}

/**
 * Ask what a typed repo offers — **on the control's own activation**, not on
 * blur (reviewer, 2026-08-12).
 *
 * This first fired on `change`, so the control could show itself enabled or
 * disabled honestly. The request was defensible — visitor-typed, no
 * enumeration, the same `/api/models/{repo}` Explore makes a moment later — but
 * the *signal* was not: blur fires on abandonment and on typos as much as on
 * intent. Someone who types `Qwen/Qwen3-` and clicks away has asked for
 * nothing, and firing there puts Hub calls on the wire for repos nobody meant
 * to open. It is the one speculative gesture in this whole feature, against a
 * promise the site makes in its own footer.
 *
 * The chicken-and-egg — the control needs to know the repo is multi-part before
 * it can offer a choice — comes apart on the observation that *enabling* is a
 * syntactic question and *answering* is a network one. `looksLikeRepoId` gates
 * the first for free; this runs on the click that wants the second.
 *
 * Returns what it found, or `undefined` when there is nothing to choose.
 */
async function fetchParts(side: "a" | "b", raw: string): Promise<PartPeek | undefined> {
  const revField = side === "a" ? revInput : compareRevInput;
  let repoId: string;
  try {
    repoId = normalizeRepoId(raw);
  } catch {
    return undefined;
  }
  // Already asked, this session, for this id: a second click should not be a
  // second request.
  const known = peeked[side];
  if (known?.repoId === repoId) return known;

  const revision = revField?.value.trim() || "main";
  const repo = await resolveRepo(repoId, revision);
  const choices = partChoicesOf(repo);
  if (!choices) return undefined;
  const peek: PartPeek = { repoId, repo, choices };
  peeked[side] = peek;
  return peek;
}

/**
 * Does this field hold something that *could* be a repo? No network.
 *
 * What decides whether the part control is offered at all. Syntax is free and
 * a lookup is not, so the control is live as soon as the text is plausibly a
 * model id and the question is only asked when it is pressed.
 */
function looksLikeRepoId(raw: string): boolean {
  try {
    return isCanonicalRepoId(normalizeRepoId(raw));
  } catch {
    return false;
  }
}

/**
 * Show the current part on each side's control, or hide a control with no
 * choice behind it.
 *
 * Called from `renderView`, so the two controls follow the same state the map
 * and the header do rather than being updated at each of the several sites that
 * can change a part.
 */
function syncPartControls(): void {
  const fill = (
    control: HTMLButtonElement | null,
    inventory: Inventory | undefined,
    which: "a" | "b",
  ): void => {
    const side = which === "a" ? "Model A" : "Model B";
    if (!control) return;
    const choices = partChoices(inventory);
    /*
     * Always on screen, disabled when there is nothing to choose (owner,
     * 2026-08-12).
     *
     * It used to appear and disappear with the repo, and a control that comes
     * and goes is harder to read than one that is simply unavailable: the row
     * re-flows under the cursor, and the visitor has to work out whether the
     * box is missing because this model has no parts or because something
     * failed. Disabled says which.
     */
    const peek = peeked[which];
    const field = which === "a" ? repoInput : compareRepoInput;
    const typed = field ? looksLikeRepoId(field.value) : false;
    control.hidden = false;
    /*
     * Live as soon as the field holds something that *looks* like a model id.
     *
     * Enabling is a syntactic question and answering is a network one, so the
     * first is decided for free here and the second waits for the click. That
     * is what lets the control be honest about being available without a
     * request going out for every repo anyone half-types.
     *
     * `singlePart` is the answer already learned for this id — a repo that
     * turned out to hold one model. Re-offering it would invite a click that
     * can only say "nothing to choose" again.
     */
    /*
     * A **loaded** side already knows the answer, so its inventory decides and
     * nothing else gets a say. The syntactic gate below is for a repo that has
     * only been typed — and letting it apply to a loaded one offered "Choose…"
     * beside a safetensors model that holds exactly one thing (owner,
     * 2026-08-12).
     */
    const settled = singlePart[which];
    /*
     * Three states, and the middle one is the one that was missing. A **loaded**
     * side is answered by its inventory. A merely *typed* one by the syntax of
     * the field. A side with a read **in flight** is neither: the inventory is
     * not there yet and syntax says nothing about what the repo holds, so
     * offering a choice invites a click that races the load — and on a
     * single-model repo it offered one that was about to be withdrawn.
     */
    control.disabled =
      loadingSide[which] ||
      (inventory
        ? !choices
        : !peek && (!typed || settled === normalizedOrUndefined(field?.value ?? "")));
    const text = control.querySelector<HTMLElement>(".part-label") ?? control;
    if (loadingSide[which]) {
      /*
       * Not "—", which is this control's word for "there is nothing to choose".
       * Mid-read that is a claim nobody has checked yet, and on a multi-part
       * repo it is about to be false.
       */
      text.textContent = "…";
      control.setAttribute("aria-label", `${side}: still reading`);
      return;
    }
    if (!choices || !inventory) {
      if (peek) {
        text.textContent = peek.chosen ?? "Pick one";
        control.setAttribute(
          "aria-label",
          `${side}: choose which model inside ${peek.repoId}`,
        );
        return;
      }
      if (typed && !control.disabled) {
        text.textContent = "Choose…";
        control.setAttribute(
          "aria-label",
          `${side}: choose which model inside this repo, if it holds several`,
        );
        return;
      }
      text.textContent = NO_PART;
      control.setAttribute(
        "aria-label",
        inventory
          ? `${side} holds a single model, so there is no part to choose`
          : `${side}: no model loaded`,
      );
      return;
    }
    const label = componentLabel(inventory.repo, inventory.component);
    text.textContent = label ?? "Pick one";
    control.setAttribute(
      "aria-label",
      `${side}: showing ${label ?? "no part"} — choose which model inside ${inventory.repo.repoId}`,
    );
  };
  fill(partControl, state.inventory, "a");
  fill(comparePartControl, state.compareInventory, "b");
}

/** What a disabled part control says: an em dash, the map's own "not here". */
const NO_PART = "—";

/**
 * Open the picker for one side, in place.
 *
 * **In place is the whole point.** A's part used to switch by navigating to a
 * URL without `?part=`, which reloads the document — so a comparison was gone
 * before the new part was even chosen, and the visitor was returned to a solo
 * map of a model they had been comparing a second ago (owner, 2026-08-12).
 * Nothing here reloads: A is re-read, `load` recomputes the diff against the B
 * inventory it still holds, and the comparison never lapses.
 */
async function openPartChooser(side: "a" | "b"): Promise<void> {
  const inventory = side === "a" ? state.inventory : state.compareInventory;
  const loaded = partChoices(inventory);
  const control = side === "a" ? partControl : comparePartControl;
  const field = side === "a" ? repoInput : compareRepoInput;

  /*
   * Nothing loaded and nothing learned yet: this click is the question, so ask
   * it now. The control says so while the request is out — a press that
   * appears to do nothing for a second is how a visitor ends up pressing it
   * again.
   */
  if ((!inventory || !loaded) && !peeked[side] && field) {
    const label = control?.querySelector<HTMLElement>(".part-label") ?? control;
    const before = label?.textContent ?? "";
    if (control) control.disabled = true;
    if (label) label.textContent = "…";
    try {
      const found = await fetchParts(side, field.value);
      if (!found) {
        // A real answer, and the control should stop offering the question.
        singlePart[side] = normalizedOrUndefined(field.value);
        setStatus(
          `${field.value.trim()} holds a single model — nothing to choose.`,
          "note",
        );
        syncPartControls();
        return;
      }
    } catch (error) {
      if (label) label.textContent = before;
      if (control) control.disabled = false;
      setStatus(messageOf(error), toneFor(error));
      return;
    }
    syncPartControls();
  }

  const peek = peeked[side];
  /*
   * A loaded side answers from its inventory; a merely typed one from the peek.
   * The loaded case wins when both exist, because the inventory is what is on
   * screen and the peek may name a repo the visitor has since typed over.
   */
  const source =
    inventory && loaded
      ? { repo: inventory.repo, choices: loaded }
      : peek
        ? { repo: peek.repo, choices: peek.choices }
        : undefined;
  if (!source) return;
  const opener = (side === "a" ? partControl : comparePartControl) ?? document.body;
  openPartDialog({
    title: partDialogTitle(source.repo.repoId, side),
    components: source.choices.components,
    kind: source.choices.kind,
    opener,
    enrich:
      source.choices.kind === "quantizations"
        ? variantsWithSizes(source.repo, source.choices.components)
        : undefined,
    onPick: (part) => {
      /*
       * Not yet loaded: remember the choice and let Explore carry it. Loading
       * here would open B on its own, before the visitor has finished saying
       * what they want compared — and on A it would discard a revision they
       * were part-way through typing.
       */
      if (!inventory || !loaded) {
        const pending = peeked[side];
        if (pending) pending.chosen = part;
        if (side === "b") state.comparePart = part;
        syncPartControls();
        return;
      }
      if (side === "a") {
        void load(repoInput?.value ?? "", revInput?.value ?? "", undefined, part).then(
          resumeCompare,
        );
        return;
      }
      void loadCompare(
        compareRepoInput?.value ?? source.repo.repoId,
        compareRevInput?.value ?? "",
        part,
      );
    },
    onDismiss: () => {},
  });
}

/** `partToReopen` against the live form and state; see that function for why. */
function submittedPart(): string | undefined {
  // Guarded rather than leaning on the shell's null-check: a function
  // declaration hoists past it, exactly as `syncActionState` notes.
  if (!repoInput) return undefined;
  let submitted: string;
  try {
    submitted = normalizeRepoId(repoInput.value);
  } catch {
    // An unparseable id is the loader's error to report, not this helper's.
    return undefined;
  }
  const kept = partToReopen(submitted, state.inventory);
  if (kept !== undefined) return kept;
  // Nothing loaded to keep, but the visitor may have picked from the bar
  // already — in which case asking again would be asking twice.
  const peek = peeked.a;
  return peek?.repoId === submitted ? peek.chosen : undefined;
}

/**
 * The two actions that need a model id are dead until there is one.
 *
 * A disabled primary is a clearer instruction than an error after the fact: it
 * says "not ready" before the visitor spends a click finding out. Compare is
 * disabled for the same reason plus a stronger one — it needs an A to measure B
 * against, so opening it first would present a form that cannot do anything.
 *
 * Driven by the input's current value rather than by app state, because the
 * visitor can clear the field after a successful load and the buttons have to
 * go back to being dead.
 */
function syncActionState(): void {
  // Guarded rather than relying on the shell's null-check: a function
  // declaration hoists past it, so that narrowing cannot reach in here.
  if (!repoInput || !loadButton || !compareToggle) return;
  const hasId = repoInput.value.trim().length > 0;
  loadButton.disabled = !hasId;
  compareToggle.disabled = !hasId || state.inventory === undefined;
}

/**
 * Editing a model id drops the revision pinned to the *previous* model.
 *
 * The revision field is populated from the URL with a resolved 40-character
 * commit SHA — that is the whole point of the `?rev=` scheme, and it is what a
 * shared link and a page reload both carry. A SHA exists in exactly one repo,
 * so typing a different model id leaves a revision that cannot resolve there,
 * and the lookup fails with "not found". Accurate about the revision, and read
 * by everyone as being about the model: the worst kind of error message,
 * because it sends you to check the thing that was right.
 *
 * Cleared unconditionally rather than only when the value looks like a SHA. A
 * hand-typed branch or tag is repo-specific too and fails the same way, and a
 * rule that keeps some revisions and drops others is one the visitor has to
 * learn. The cost is retyping a revision in the rare order (revision first,
 * then edit the id); the benefit is that the common order cannot produce a
 * misleading failure at all.
 *
 * Only ever fires on a real edit: assigning `.value` does not dispatch `input`,
 * which is what keeps the deep-link boot path below — where the repo id and its
 * pinned revision are written programmatically, in that order — from clearing
 * the revision it was just given. That reproducibility is what the URL scheme
 * exists for, so it is asserted in `url-state.test.ts` rather than left to this
 * comment.
 */
function clearPinnedRevision(field: HTMLInputElement | null): void {
  if (field) field.value = "";
}

// One listener, not two: `syncActionState` already owns this event, and a
// second handler on the same input is two things that can disagree about what
// the field now says.
repoInput.addEventListener("input", () => {
  clearPinnedRevision(revInput);
  syncActionState();
  /*
   * Whether the part control is *available* is a syntactic question about this
   * field, so it is re-answered as the field changes. Purely local — nothing
   * here touches the network; the lookup waits for the control to be pressed.
   */
  peeked.a = undefined;
  syncPartControls();
});

// The compare slot has the identical trap with its own pair of fields — B's
// revision is pinned to B's repo, and the shared-comparison link carries both.
compareRepoInput.addEventListener("input", () => {
  clearPinnedRevision(compareRevInput);
  // Same as A's: syntax decides availability, and editing the id invalidates
  // whatever was learned about the previous one.
  peeked.b = undefined;
  state.comparePart = undefined;
  syncPartControls();
});

filterInput.addEventListener("input", () => {
  state.filter = filterInput.value;
  renderList();
});

partControl?.addEventListener("click", () => void openPartChooser("a"));
comparePartControl?.addEventListener("click", () => void openPartChooser("b"));

compareToggle.addEventListener("click", () => {
  // Opening asks first; closing is unconditional. A dialog on the way *out*
  // would put a question between the visitor and a control they pressed to
  // dismiss something.
  if (state.compareOpen) {
    setCompareOpen(false);
    return;
  }
  openCompareWith();
});

// The second slot is inside the form, so Enter in either of its inputs would
// otherwise submit the form and reload A, discarding B and the diff. Handled
// here instead, which also means Enter in the B field reads only B rather than
// re-reading a model that has not changed.
for (const input of [compareRepoInput, compareRevInput]) {
  input.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    void loadCompare(compareRepoInput.value, compareRevInput.value);
  });
}

compareClear.addEventListener("click", () => {
  compareRepoInput.value = "";
  compareRevInput.value = "";
  setCompareOpen(false);
});

// Deep link: /owner/name?rev=<sha>&tensor=… reproduces what was shared, as does
// the legacy ?repo=owner/name form it replaced (plan §22). `linked` is parsed at
// the top of this file.
if (linked.repoId) {
  repoInput.value = linked.repoId;
  syncActionState();
  const linkedRev = linked.rev ?? "";
  revInput.value = linkedRev;
  // Rewrite a legacy link to the path form immediately rather than waiting for
  // the load to succeed: an old link that lands on a rate-limited Hub should
  // still leave the visitor holding the new URL. `replaceState` makes this a
  // 301 that costs no request and no history entry.
  if (linked.legacy) history.replaceState(null, "", buildUrl(linked));

  // A shared comparison names both sides. Open the slot before A loads so the
  // page does not visibly rearrange itself once B arrives, but load B *after* A
  // resolves — the diff needs both inventories, and issuing four header reads at
  // once against two repos is exactly the stampede the concurrency cap exists to
  // prevent.
  if (linked.compareRepoId) {
    setCompareOpen(true);
    compareRepoInput.value = linked.compareRepoId;
    compareRevInput.value = linked.compareRev ?? "";
    /*
     * B's part, held before B is read.
     *
     * The bar has no field for it, so without this the only copy lives in the
     * `linked` object — and a shared link whose *A* side needs a part choice
     * goes through the picker first, by which point `resumeCompare` has no way
     * to know which model inside B was meant and would ask a question the URL
     * had already answered.
     */
    state.comparePart = linked.comparePart;
  }

  /*
   * Assigning `.value` fires no `input`, so the listeners that keep the part
   * controls in step with the fields never run on this path. Synced once here,
   * for the same reason the pre-flight syncs on its early exits.
   */
  syncPartControls();

  void load(linked.repoId, linkedRev, linked.tensor, linked.part).then(() => {
    if (linked.compareRepoId && state.inventory) {
      // `bpart` too, or a shared comparison against a GGUF repo reopens as
      // "pick one of 25" — the state-losing failure `?part=` exists to
      // prevent, on B's side.
      void loadCompare(
        linked.compareRepoId,
        linked.compareRev ?? "",
        linked.comparePart,
      );
    }
  });
} else {
  renderList();
  renderView();
}
